import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Avatar,
  Chip,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Container,
  Skeleton,
  Divider,
  Alert,
  Popover,
  Stack,
  Tooltip,
  Link,
  CircularProgress,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Archive as ArchiveIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Share as ShareIcon,
  Warning as WarningIcon,
  Place as PlaceIcon,
  Search as SearchIcon,
  ViewList as TimelineIcon,
  Description as SoapIcon,
  People as PeopleIcon,
  InfoOutlined as InfoIcon,
  OpenInNew as OpenInNewIcon,
  Description as CertificateIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { INSTANT_FORMATS, computeAgeYears, formatCalendarDate, formatDob, isDobKnown } from '../../utils/dateUtils';
import { useClinicTimezone, useFormatInstant } from '../../hooks/useClinicTimezone';
import { cogginsHeaderStatus, groupMedicationsByStartDay, overdueVaccinationCount, pickCogginsSummary } from './chartDates';
import type { CogginsExpiryState } from './chartDates';
import { downloadAuthedBlob } from '../../hooks/useAuthedBlobUrl';

/** Solid-fill twins of PatientHeaderCard's pale BADGE ramp, in this layout's idiom. */
const COGGINS_CHIP_BG: Record<CogginsExpiryState, string> = {
  unknown: '#757570',
  current: '#2E7D32',
  soon: '#B26A00',
  urgent: '#D32F2F',
  expired: '#D32F2F',
  positive: '#B71C1C',
};
import {
  COGGINS_NO_EXPIRY_LABEL,
  isCogginsLabResultRow,
  isCogginsName,
  isPositiveCogginsResult,
} from '../../utils/coggins';
import { horseApi, medicalRecordsApi, appointmentApi, Horse, MedicalRecord, billingApi, InvoiceStats, clientApi, Client, Owner, treatmentSheetApi } from '../../utils/api';
import { useAuth } from '../../auth/useAuth';
import { useRBAC } from '../../auth/useRBAC';
import { patientChartControls, patientChartPanels } from '../../auth/pageControls';
import { CHART_PANELS, noAccessMessage } from '../../auth/screenAccess';
import NoAccess from '../../components/NoAccess';
import DischargeSummarySimplified from '../../components/DischargeSummary/DischargeSummarySimplified';
import HoofIcon from '../../components/HoofIcon';
import HorsePhotoAvatar from '../../components/HorsePhotoAvatar';
import HorseFormDialog from '../../components/HorseFormDialog';
import SoapNoteDialog from '../../components/SoapNote/SoapNoteDialog';
import ShareRecordsDialog from '../../components/StableDocs/ShareRecordsDialog';
import { useStableDocsShares } from '../../hooks/useStableDocsShares';
import SoapNoteCard from '../../components/SoapNote/SoapNoteCard';
import PatientFilesTab from '../../components/PatientFiles/PatientFilesTab';
import { usePatientFiles } from '../../components/PatientFiles/usePatientFiles';
import { countSoapAttachmentsByNote } from '../../components/PatientFiles/patientFiles';
import { soapNotesService } from '../../services/soapNotesService';
import { SoapNote } from '../../types/soap';
// Imaging studies + requests now live entirely inside LabsDiagnosticsPanel.
// (Legacy ImagingRequestDialog deleted; ImagingStudyCard moved to the panel.)
import UnallocatedSuggestionBanner from '../../components/Inbox/UnallocatedSuggestionBanner';
import MedicationDialog from '../../components/Medication/MedicationDialog';
import MedicationCard from '../../components/Medication/MedicationCard';
import LogRefillRequestDialog from '../RefillRequests/LogRefillRequestDialog';
import { medicationService } from '../../services/medicationService';
import { inventoryService } from '../../services/inventoryService';
import ProcedureDialog from '../../components/Procedure/ProcedureDialog';
import ProcedureCard from '../../components/Procedure/ProcedureCard';
import VaccinationDialog from '../../components/Vaccination/VaccinationDialog';
import EditVaccinationDialog from '../../components/Vaccination/EditVaccinationDialog';
import VaccinationCard from '../../components/Vaccination/VaccinationCard';
import PatientVaccinationsTab from '../../components/Vaccination/PatientVaccinationsTab';
import { VaccinationService, toCreateVaccinationDto } from '../../services/vaccinationService';
import { ProcedureService } from '../../services/procedureService';
import { serviceCatalogService, ServiceCatalogItem } from '../../services/serviceCatalogService';
import ClientCommunicationDialog, { ClientCommunication } from '../../components/ClientCommunication/ClientCommunicationDialog';
import VisitNoteDialog from '../../components/VisitNote/VisitNoteDialog';
import ClientCommunicationList from '../../components/ClientCommunication/ClientCommunicationList';
import { clientCommunicationService } from '../../services/clientCommunicationService';
import { DischargeSummaryService } from '../../services/dischargeSummaryService';
import api from '../../utils/api';
import { formatDollars } from '../../utils/money';
import { formatBreed } from '../../utils/breed';
import AlertManagementDialog from '../../components/AlertManagementDialog';
import LabsDiagnosticsPanel from '../../components/LabsDiagnosticsPanel';
import ProblemListPanel from '../../components/ProblemListPanel';
import RecallRemindersPanel from '../../components/RecallRemindersPanel';
import { useSnackbar } from 'notistack';
import { TimelineEvent, toTimelineEvent, safeDate } from './components/TimelineRow';
import EventDetailDrawer from './components/EventDetailDrawer';
import ContextRail from './components/ContextRail';
import TimelineSpine from './components/TimelineSpine';
import FilterChips, { ActiveFilter } from './components/FilterChips';
import QuickActionStrip from './components/QuickActionStrip';
import ChartLayoutToggle from './ChartLayoutToggle';
import { useChartLayout } from './useChartLayout';
import { markPatientViewed } from '../PatientList/recentlyViewed';
import { withOfflineFallback, skipWhenOffline, readHorse, readByHorse, readActiveByHorse, readInvoicesPageByHorse, readHorseInvoiceStats, executeWrite, executeUpdate, executeDelete, OfflineWriteBlockedError, useIsOffline } from '../../offline';

const HorseProfileModern: React.FC = () => {
  const { horseId } = useParams<{ horseId: string }>();
  const navigate = useNavigate();
  const { layout, setLayout } = useChartLayout();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const rbac = useRBAC();
  const { permissionsPending, permissionsUnavailable, claims } = rbac;
  /**
   * THE CHART'S WRITE CONTROLS AND ITS PANELS, from the one tested map (INV-108 (d)).
   *
   * `auth/pageControls.patientChartControls` carries every gate with the controller line it
   * copies; `patientChartPanels` carries the READ gate for each section, keyed to
   * `CHART_PANELS`. Both are entered through `controlAnswerKnown`, so a control is hidden and a
   * panel refused ONLY on a known answer that lacks the slug — pending, unreadable and
   * identity-unknown render this page exactly as it rendered before any of this existed.
   */
  const can = patientChartControls(rbac);
  const isOffline = useIsOffline();
  const panels = patientChartPanels(rbac);
  // INSTANT renders (discharge, treatment-sheet, invoice and last-visit stamps)
  // go through the CLINIC zone, never the browser's — DATE-KINDS.md §0.
  const formatInstant = useFormatInstant();
  // ...and so does the Coggins chip's "today" (§2.9).
  const clinicTz = useClinicTimezone();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [anchorPos, setAnchorPos] = useState<{ top: number; left: number } | null>(null);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [alertsDialogOpen, setAlertsDialogOpen] = useState(false);
  const [editPatientOpen, setEditPatientOpen] = useState(false);
  const [showDischargeDialog, setShowDischargeDialog] = useState(false);
  // Whether the discharge dialog should open straight into the editor (set by
  // the timeline's inline "Edit" action; cleared for plain view/open paths).
  const [dischargeEditMode, setDischargeEditMode] = useState(false);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
  // Identity popover — color/microchip/weight/etc. live behind a tap from the
  // header to keep the sticky strip slim. Vets rarely need these mid-visit.
  const [identityAnchor, setIdentityAnchor] = useState<null | HTMLElement>(null);
  const [ownerAnchor, setOwnerAnchor] = useState<null | HTMLElement>(null);
  // Tracks which owner row (by index) is being resolved to a Client so we can
  // show a spinner and ignore double-clicks while the lookup is in flight.
  const [resolvingOwnerIdx, setResolvingOwnerIdx] = useState<number | null>(null);
  const [soapDialogOpen, setSoapDialogOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedSoapNote, setSelectedSoapNote] = useState<SoapNote | null>(null);
  const [medicationDialogOpen, setMedicationDialogOpen] = useState(false);
  // Opens the LogRefillRequestDialog with this horse preselected — staff
  // uses this when an owner calls or walks in asking for a refill.
  const [logRefillOpen, setLogRefillOpen] = useState(false);
  const [procedureDialogOpen, setProcedureDialogOpen] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState<any>(null);
  const [vaccinationDialogOpen, setVaccinationDialogOpen] = useState(false);
  const [editingVaccination, setEditingVaccination] = useState<any | null>(null);
  const [clientCommDialogOpen, setClientCommDialogOpen] = useState(false);
  const [visitNoteDialogOpen, setVisitNoteDialogOpen] = useState(false);
  const [editingVisitNote, setEditingVisitNote] = useState<MedicalRecord | null>(null);
  const [clinicalSearchTerm, setClinicalSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedTagFilter, setSelectedTagFilter] = useState<string | null>(null);
  const [showAllOwners, setShowAllOwners] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [invoiceNotification, setInvoiceNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error';
    invoiceId?: string;
  }>({ open: false, message: '', type: 'success' });
  const [confirmAction, setConfirmAction] = useState<{ message: string; onConfirm: () => void } | null>(null);

  // Check permissions
  // The permission arms used to name slugs that were in NO vocabulary
  // (`manage:horses`, `delete:medical-records`) and so only ever resolved through
  // `manage:all`. Re-pointed by the P3 sweep to the slugs the endpoints actually
  // require: POST/PUT/DELETE /horses → manage:patients, the record DELETEs →
  // manage:medical_records_archive.
  //
  // ⚠ P6 DELETED the role arms on the two flags below (ADR-0035 A4) — same rule, same
  // reason and the same wording as HorseProfileClassic, which is where the full note
  // lives: the OR meant the permission arm could never bind, and ANDing the quartet
  // instead would have taken the affordances from a CSR who holds both slugs.
  //
  // The in-flight window the role arm used to cover is now covered at the CONTROLS.
  // hasPermission answers false until the /users/me read lands (useRBAC.ts:269-274),
  // so on this layout the Row-1 Edit IconButton renders MOUNTED and DISABLED and the
  // invoice panel renders a Skeleton while permissionsPending is true — ChatPanel's
  // idiom (ChatPanel.tsx:413-422), without its text — instead of blinking out and
  // back on every reload. After the read has ANSWERED or FAILED both flags fail
  // CLOSED, explained by the shell's PermissionsUnavailableBanner. Pinned in
  // auth/useRBAC.test.tsx and in backend/src/auth/permissions-docs.spec.ts.
  const canEdit = can.editPatient;
  // THE CLINICAL ADDS ARE A DIFFERENT CAPABILITY FROM THE PATIENT EDITS (P5, Ali default #64) —
  // same rule and same wording as HorseProfileClassic, which is where the full note lives. On this
  // layout the adds live in QuickActionStrip rather than in the tab headers, so this flag is what
  // that component renders its clinical cards from; `canEdit` (manage:patients) keeps the Row-1
  // Edit-patient IconButton and the Manage-alerts button. Every clinical add here POSTs to a route
  // that requires `manage:medical_records`, which a CSR does not hold and all four legacy roles do.
  // ⚠ Permission-only, no role arm — see the Classic note for the reason and the in-flight cost.
  const canAddClinical = can.addClinicalRecord;
  // The slug the thirteen archive routes require, and nothing else (ADR-0035 A10, as on the
  // iPhone). Pinned by useRBAC.test.tsx ("both HorseProfile screens gate canArchive…").
  const canArchive = can.archive;
  /**
   * ⚠ THE FIRST OF THE THREE STAGING DEFECTS (Ali, 2026-09-21). This read
   *
   *   hasRole('VETERINARIAN') || hasRole('VETERINARY_TECHNICIAN') || hasRole('OWNER') || hasRole('ADMIN')
   *
   * — four roles, no permission — while `POST /soap-notes` declares `manage:medical_records`
   * (soap-notes.controller.ts:44). With BOTH medical-record abilities switched off, the person
   * was still one of those four roles, so **New SOAP Note** was still on the chart and its save
   * 403'd. The quieter half was live too: anybody an owner granted the slug to outside the
   * quartet never saw the button at all, and nobody reports a button they have never seen.
   * It is the permission now, through the map, exactly as the handler spells it.
   */
  const canCreateSoapNote = can.newSoapNote;
  // Permission-ONLY since P6, mirroring HorseProfileClassic. ONE consumer here since
  // the P6 fix round: the New-Invoice button inside the Invoices panel. It is the
  // WRITE slug — POST /billing and POST /billing/generate-from-horse/:horseId declare
  // it (billing.controller.ts:104, :882) — so it gates the button and nothing else.
  const canManageBilling = can.newInvoice;
  // ...and the READ beside it, which the first real manage:charges revoke proved is a
  // different question. GET /billing (:137) and GET /billing/stats/horse/:horseId
  // (:890) declare view:billing, and the editor catalog's REQUIRES_VIEW makes
  // manage:charges a slug you may only hold ON TOP of this one — so gating the invoice
  // LIST on the write slug hid a read the API grants (the lag PatientFilesTab warns
  // about) and protected nothing: /billing carries no client-side gate, so the same
  // user opens the same invoices from the Invoicing menu item. This flag gates the
  // panel and its query; a refusal is explained there rather than left blank.
  const canViewBilling = panels.invoices;

  // Fetch horse data
  const { data: horse, isLoading: horseLoading, isError: horseError } = useQuery<Horse>(
    ['horse', horseId],
    withOfflineFallback(
      () => horseApi.get(horseId!),
      () => readHorse(horseId!) as any,
    ),
    { enabled: !!horseId }
  );

  // Push to the Patients-page "Recently viewed" rail. Fires when the horse
  // record actually loads, so we don't seed the cache from a stale URL.
  useEffect(() => {
    if (!horse) return;
    markPatientViewed(user?.uid, {
      id: horse.id,
      name: horse.name,
      patientId: (horse as any).patientId,
      breed: formatBreed(horse),
      ownerName: horse.owners?.[0]?.name,
      status: horse.status,
    });
  }, [horse?.id]);

  // Owners live as JSONB on the horse (no client id), but each is synced to a
  // Client record server-side (findOrCreate by name/email). To open the owner
  // page we resolve that Client at click time: search by name, then prefer an
  // exact name match disambiguated by email/phone. If we can't pin a single
  // client we still land the user on the owner page filtered by name.
  const openOwnerPage = async (owner: Owner, idx: number) => {
    if (resolvingOwnerIdx !== null) return; // a lookup is already in flight
    const name = owner.name?.trim();
    if (!name) return;
    const norm = (s?: string) => (s || '').trim().toLowerCase();
    setResolvingOwnerIdx(idx);
    try {
      const matches = await clientApi.list(name);
      // Only trust an EXACT name match. The backend /clients?search= does a
      // substring match, so a lone fuzzy hit (e.g. "Samuel Smith" for owner
      // "Sam") must NOT be treated as the definitive client — that would open
      // the wrong owner's record. Disambiguate exact-name ties by email/phone.
      const exact = matches.filter((c) => norm(c.name) === norm(name));
      let chosen: Client | undefined;
      if (exact.length === 1) {
        chosen = exact[0];
      } else if (exact.length > 1) {
        if (owner.email) chosen = exact.find((c) => norm(c.email) === norm(owner.email));
        if (!chosen && owner.phone) chosen = exact.find((c) => norm(c.phone) === norm(owner.phone));
      }
      if (chosen) {
        navigate('/clients', { state: { selectedClientId: chosen.id } });
      } else {
        navigate('/clients', { state: { selectedClientSearch: name } });
      }
    } catch {
      // Lookup failed — still take the user to the owner page filtered by name.
      navigate('/clients', { state: { selectedClientSearch: name } });
    } finally {
      setResolvingOwnerIdx(null);
    }
  };

  // Fetch logged-in user's profile
  const { data: profile } = useQuery(['my-profile'], () => api.get('/users/me').then(res => res.data));

  // Toggle include archived
  const [showArchived, setShowArchived] = useState(false);

  // All per-horse queries below share `staleTime: 60_000` so switching back
  // to a horse you viewed in the last minute loads instantly from cache
  // instead of re-firing the whole fetch storm.
  const PER_HORSE_STALE_TIME = 60_000;

  // Fetch medical records (always fetch all, filter client-side)
  const { data: allRecords = [], isLoading: recordsLoading } = useQuery<MedicalRecord[]>(
    ['horse-records', horseId],
    withOfflineFallback(
      () => horseApi.getMedicalRecords(horseId!, true),
      () => readByHorse('medicalRecords', horseId!) as any,
    ),
    // ⚠ `panels.medicalRecords` GATES THE QUERY, not just the markup. A refused read is an
    // answer (INV-108 (a)) and `withOfflineFallback` no longer serves the replica for one — but
    // the request still goes out, and this chart fires sixteen of them on open. The panel says
    // so in place; the read does not happen at all.
    { enabled: !!horseId && panels.medicalRecords, staleTime: PER_HORSE_STALE_TIME },
  );

  // Filter medical records based on showArchived
  const records = useMemo(() => {
    return showArchived 
      ? allRecords.filter(rec => rec.isArchived)
      : allRecords.filter(rec => !rec.isArchived);
  }, [allRecords, showArchived]);

  // Fetch attachments linked to medical records for this horse (for download buttons)
  const { data: recordAttachments = [] } = useQuery<any[]>(
    ['record-attachments', horseId],
    () => api.get('/attachments', { params: { horseId, parentType: 'medical_record' } }).then(r => r.data),
    // GET /attachments → view:medical_records (attachments.controller.ts:60), which is the FILES
    // panel's slug — same gate, so the download buttons on record cards disappear with the files.
    { enabled: !!horseId && skipWhenOffline() && panels.files, staleTime: PER_HORSE_STALE_TIME },
  );

  // All attachments for this horse — drives the "Files" chip badge count.
  // (The Files panel re-uses the same query key, so this never double-fetches.)
  const { attachments: patientFiles } = usePatientFiles(horseId, { canRead: panels.files });
  const filesCount = patientFiles.length;
  // soapNoteId -> attached-file count, for the paperclip chip on collapsed timeline rows.
  const soapAttachmentCounts = useMemo(
    () => countSoapAttachmentsByNote(patientFiles as any[]),
    [patientFiles],
  );

  // Map parentId (medical record id) → attachment for O(1) lookup in RecordCard
  const recordAttachmentMap = useMemo(() => {
    const map: Record<string, any> = {};
    recordAttachments.forEach((a: any) => { if (a.parentId) map[a.parentId] = a; });
    return map;
  }, [recordAttachments]);

  // Fetch SOAP notes for this horse (always fetch all, filter client-side)
  const { data: allSoapNotes = [], isLoading: soapNotesLoading } = useQuery<SoapNote[]>(
    ['soap-notes', horseId],
    withOfflineFallback(
      () => soapNotesService.findAll({ horseId: horseId! }),
      () => readByHorse('soapNotes', horseId!) as any,
    ),
    // GET /soap-notes → view:medical_records. SOAP notes are part of the record timeline, so
    // they ride the same panel row rather than one of their own.
    { enabled: !!horseId && panels.medicalRecords, staleTime: PER_HORSE_STALE_TIME },
  );

  // Filter SOAP notes based on showArchived
  const soapNotes = useMemo(() => {
    return showArchived
      ? allSoapNotes.filter((note: any) => note.isArchived)
      : allSoapNotes.filter((note: any) => !note.isArchived);
  }, [allSoapNotes, showArchived]);

  // Fetch procedures for this horse (always fetch all, filter client-side)
  const { data: procedures = [], isLoading: proceduresLoading } = useQuery(
    ['procedures', horseId],
    withOfflineFallback(
      () => ProcedureService.getAll({ horseId: horseId!, includeArchived: true }),
      () => readByHorse('procedures', horseId!) as any,
    ),
    { enabled: !!horseId && panels.procedures, staleTime: PER_HORSE_STALE_TIME },
  );

  // Fetch medications for this horse (always fetch all, filter client-side)
  const { data: medications = [], isLoading: medicationsLoading } = useQuery(
    ['medications', horseId],
    withOfflineFallback(
      () => medicationService.getAll({ horseId: horseId!, includeArchived: true }),
      () => readByHorse('medications', horseId!) as any,
    ),
    { enabled: !!horseId && panels.medications, staleTime: PER_HORSE_STALE_TIME },
  );

  // Total non-archived medications on this horse. Passed to MedicationCard so
  // the delete button can be disabled when this drops to 1 — preserving at
  // least one medication record on the horse for audit/history.
  const totalActiveMedications = useMemo(
    () => (medications as any[]).filter((m: any) => !m.isArchived).length,
    [medications],
  );

  // Fetch vaccinations for this horse
  const { data: allVaccinations = [] } = useQuery(
    ['vaccinations', horseId],
    withOfflineFallback(
      () => VaccinationService.getAll({ horseId: horseId!, includeArchived: true }),
      () => readByHorse('vaccinations', horseId!) as any,
    ),
    { enabled: !!horseId && panels.vaccinations, staleTime: PER_HORSE_STALE_TIME },
  );

  // Fetch low-stock inventory items
  // Low-stock inventory flags used to highlight medication/vaccination cards.
  // This used to `refetchInterval: 60000` (poll every minute) which was
  // pointless on a detail page — inventory doesn't change that often and the
  // user isn't watching for real-time updates here. Now relies on the
  // global 30s staleTime + 5min cacheTime.
  const { data: lowStockItems = [] } = useQuery(
    ['low-stock-inventory'],
    () => inventoryService.getLowStockItems(),
    // GET /inventory/low-stock → view:inventory (inventory.controller.ts:59). Not a panel and so
    // no sentence: without the slug the med/vax cards lose a low-stock tint, which is a missing
    // highlight rather than a missing section. What it must not do is 403 on every chart opened.
    { enabled: skipWhenOffline() && can.lowStockHighlight, staleTime: 5 * 60_000 },
  );

  // Fetch client communications for this horse
  const { data: clientCommunications = [] } = useQuery<ClientCommunication[]>(
    ['client-communications', horseId],
    withOfflineFallback(
      () => clientCommunicationService.getByHorseId(horseId!),
      () => readByHorse('clientCommunications', horseId!) as any,
    ),
    { enabled: !!horseId && panels.communications, staleTime: PER_HORSE_STALE_TIME },
  );

  // What has been sent from this chart with StableDocs (timeline entries).
  const { data: stableDocsShares = [] } = useStableDocsShares(
    horseId,
    panels.medicalRecords && can.shareRecords && !permissionsPending,
  );

  // Fetch discharge summaries for this horse
  const { data: dischargeSummaries = [] } = useQuery(
    ['discharge-summaries', horseId],
    withOfflineFallback(
      () => DischargeSummaryService.getByHorseId(horseId!),
      () => readByHorse('dischargeSummaries', horseId!) as any,
    ),
    { enabled: !!horseId && panels.dischargeSummaries, staleTime: PER_HORSE_STALE_TIME },
  );

  // Fetch this horse's treatment sheets (hospitalization history). Online-first.
  const { data: horseTreatmentSheets = [] } = useQuery(
    ['horse-treatment-sheets', horseId],
    () => treatmentSheetApi.getHorseSheets(horseId!),
    { enabled: !!horseId && panels.treatmentSheets, staleTime: PER_HORSE_STALE_TIME },
  );

  // Fetch invoice stats for this horse
  const { data: horseInvoiceStats } = useQuery<InvoiceStats>(
    ['horse-invoice-stats', horseId],
    withOfflineFallback(
      () => billingApi.getHorseStats(horseId!),
      () => readHorseInvoiceStats(horseId!) as Promise<InvoiceStats>,
    ),
    // GET /billing/stats/horse/:horseId → view:billing (billing.controller.ts:909), the same
    // slug as the list beside it. It was ungated, so a person without the money slug 403'd on
    // every chart open even though the panel already refused to show them anything.
    { enabled: !!horseId && panels.invoices }
  );

  // Fetch invoice list — only loaded when the Invoices tab is active.
  const { data: horseInvoiceList } = useQuery<any>(
    ['horse-invoices', horseId],
    withOfflineFallback(
      () => billingApi.listInvoicesPaginated({ page: 1, limit: 50, horseId: horseId! }),
      () => readInvoicesPageByHorse(horseId!) as any,
    ),
    // ⚠ P6 changed WHEN this fires, and the fix round changed WHICH slug decides. This
    // is the read GET /billing declares `view:billing` for, so `canViewBilling` is the
    // honest flag — asking on `manage:charges` refused to fetch a list the API would
    // have served. Both flags are permission-ONLY, so the query no longer starts on the
    // role the token carried: it waits for the /users/me read and fires when `enabled`
    // flips (React Query v3 re-evaluates it every render). The wait is deliberate —
    // firing on a pending answer asks for invoices on behalf of a user the read may be
    // about to refuse. The Skeleton arm further down covers that window on screen.
    //
    // ⚠ `!permissionsPending` IS NOW EXPLICIT rather than implied. `canViewBilling` comes from
    // the shared panel map, which shows every panel while the answer is pending (so that a cold
    // load renders the chart rather than twelve refusals) — and for THIS panel the established
    // behaviour is the opposite: wait, show a Skeleton, and do not ask for a client's invoices
    // on behalf of a user the read may be about to refuse. Both are right; they just have to be
    // spelled separately now that the flag no longer carries the pending arm on its own.
    { enabled: !!horseId && activeFilter === 'invoice' && !permissionsPending && canViewBilling, staleTime: PER_HORSE_STALE_TIME }
  );

  // Fetch appointments for THIS horse to determine last visit. Previously
  // this pulled every appointment in the clinic — a serious perf bug on
  // large tenants. Now scoped to the horse via a query param.
  const { data: horseAppointmentsForLastVisit = [] } = useQuery(
    ['horse-appointments', horseId],
    withOfflineFallback(
      () => appointmentApi.list({ horseId: horseId! }),
      () => readByHorse('appointments', horseId!) as any,
    ),
    // GET /appointments → view:appointments. Like low-stock, this is a header STAMP ("last
    // visit") rather than a panel, so its absence is a blank field and not a sentence.
    { enabled: !!horseId && can.lastVisit, staleTime: 30_000 },
  );

  // Imaging studies fetch moved into LabsDiagnosticsPanel — it now owns
  // the unified "pending requests + completed studies + lab results" view.

  const clinicId = claims?.clinicId;

  const { data: clinicSettings } = useQuery(
    ['clinic-settings', clinicId],
    async () => {
      const response = await api.get(`/clinics/${clinicId}`);
      return {
        showAllMedications: response.data.showAllMedications ?? true,
        showAllVaccinations: response.data.showAllVaccinations ?? true,
        showAllProcedures: response.data.showAllProcedures ?? true,
      };
    },
    { enabled: !!clinicId, staleTime: 5 * 60 * 1000 }
  );

  // serviceCatalog is a replicated entity and serviceCatalogService.getAll is
  // offline-aware internally (withOfflineFallback → readServiceCatalog), so the
  // three catalog queries stay enabled offline — gating them would open the
  // med/vax/procedure dialogs with an empty priced group, and a med picked
  // without its catalog price syncs with unitPrice undefined (silent $0 bill).
  const { data: medicationCatalog = [] } = useQuery<ServiceCatalogItem[]>(
    ['service-catalog', 'medication'],
    () => serviceCatalogService.getAll({ category: 'medication' }),
    { staleTime: 5 * 60 * 1000 }
  );

  const { data: vaccinationCatalog = [] } = useQuery<ServiceCatalogItem[]>(
    ['service-catalog', 'vaccination'],
    () => serviceCatalogService.getAll({ category: 'vaccination' }),
    { staleTime: 5 * 60 * 1000 }
  );

  const { data: procedureCatalog = [] } = useQuery<ServiceCatalogItem[]>(
    ['service-catalog', 'procedure'],
    () => serviceCatalogService.getAll({ category: 'procedure' }),
    { staleTime: 5 * 60 * 1000 }
  );

  // Tab badge counts — share react-query keys with the panels so no duplicate fetches.
  // Lab results, problems, and recalls are replicated offline (Bucket C chart
  // tables), so their badge counts fall back to the local replica when the
  // network is unavailable — otherwise an empty array would render "0", which
  // reads clinically as "no labs / no active problems" (active misinformation).
  const { data: labResultsForCount = [] } = useQuery<any[]>(
    ['lab-results', horseId],
    withOfflineFallback(
      () => api.get('/lab-results', { params: { horseId } }).then(r => r.data),
      () => readActiveByHorse('labResults', horseId!) as any,
    ),
    { enabled: !!horseId && panels.labs },
  );
  // The diagnostic-records leg of the Labs badge counts medical-record ROWS
  // (diagnostic / imaging / outside_lab), which ARE replicated — so offline we
  // recreate the same type filter against the local medicalRecords table to keep
  // the count consistent with online.
  const DIAG_RECORD_TYPES = ['diagnostic', 'imaging', 'outside_lab'];
  const { data: diagRecordsForCount = [] } = useQuery<any[]>(
    ['med-records-diag', horseId],
    withOfflineFallback(
      () => api.get('/medical-records', { params: { horseId, types: 'diagnostic,imaging,outside_lab' } }).then(r => r.data),
      async () => {
        const rows = await readByHorse('medicalRecords', horseId!);
        return rows.filter((r: any) => DIAG_RECORD_TYPES.includes(r.type)) as any;
      },
    ),
    { enabled: !!horseId && panels.labs },
  );
  // Imaging studies + open imaging requests roll up into the Labs &
  // Diagnostics tab badge so the count matches what's actually visible inside
  // the panel (one unified Pending + Results timeline). These come from the
  // Keystone integration and are NOT replicated offline, so they stay
  // skip-when-offline gated (the badge falls back to the replicated legs above).
  const { data: keystoneStudiesForCount = [] } = useQuery<any[]>(
    ['horse-keystone-studies', horseId],
    () => api.get(`/keystone/horse/${horseId}/studies`).then(r => r.data),
    // keystone.controller.ts `getStudiesForHorse` :1035 → view:medical_records, same as every
    // other feed the Labs panel merges. Nothing about the integration makes it a stricter gate.
    { enabled: !!horseId && skipWhenOffline() && panels.labs },
  );
  const { data: imagingRequestsForCount = [] } = useQuery<any[]>(
    ['imaging-requests', horseId],
    () => api.get('/keystone/imaging-requests', { params: { horseId } }).then(r => r.data),
    { enabled: !!horseId && skipWhenOffline() && panels.labs },
  );
  const { data: problemsForCount = [] } = useQuery<any[]>(
    ['problem-list', horseId],
    withOfflineFallback(
      () => api.get('/problem-list', { params: { horseId } }).then(r => r.data),
      () => readActiveByHorse('problemLists', horseId!) as any,
    ),
    { enabled: !!horseId && panels.problems },
  );
  const { data: recallsForCount = [] } = useQuery<any[]>(
    ['recall-reminders', horseId],
    withOfflineFallback(
      () => api.get('/recall-reminders', { params: { horseId } }).then(r => r.data),
      () => readByHorse('recallReminders', horseId!) as any,
    ),
    { enabled: !!horseId && panels.recalls },
  );
  // Open imaging requests = DRAFT or SUBMITTED (not yet finalized).
  // Cancelled drafts don't contribute to the count.
  const openImagingRequestsCount = imagingRequestsForCount.filter(
    (r: any) => r.submissionStatus === 'DRAFT' || r.submissionStatus === 'SUBMITTED',
  ).length;
  const labsCount =
    labResultsForCount.length +
    diagRecordsForCount.length +
    keystoneStudiesForCount.length +
    openImagingRequestsCount;

  // Archive record mutation (soft delete)
  const archiveRecord = useMutation(
    (id: string) =>
      executeUpdate({
        entityType: 'medicalRecord',
        table: 'medicalRecords',
        entityId: id,
        payload: { isArchived: true, archivedAt: new Date().toISOString() },
        networkFn: () => medicalRecordsApi.archive(id),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['horse-records', horseId]);
        setAnchorPos(null);
        enqueueSnackbar('Record archived', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to archive record', { variant: 'error' });
      },
    }
  );

  const restoreRecord = useMutation(
    (id: string) =>
      executeUpdate({
        entityType: 'medicalRecord',
        table: 'medicalRecords',
        entityId: id,
        payload: { isArchived: false, archivedAt: null },
        networkFn: () => medicalRecordsApi.restore(id),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['horse-records', horseId]);
        enqueueSnackbar('Record restored', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to restore record', { variant: 'error' });
      },
    }
  );

  const createClientCommunication = useMutation(
    (data: Omit<ClientCommunication, 'id'>) =>
      executeWrite<ClientCommunication>({
        entityType: 'clientCommunication',
        table: 'clientCommunications',
        payload: data,
        networkFn: () => clientCommunicationService.create(data),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client-communications', horseId]);
        enqueueSnackbar('Communication recorded', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to save communication', { variant: 'error' });
      },
    }
  );

  const createVisitNote = useMutation(
    (data: { horseId: string; type: string; date: string; notes: string; tags: string[] }) =>
      executeWrite({
        entityType: 'medicalRecord',
        table: 'medicalRecords',
        payload: data,
        networkFn: () => medicalRecordsApi.create(data),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['horse-records', horseId]);
        enqueueSnackbar('Visit note saved', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to save visit note', { variant: 'error' });
      },
    }
  );

  const updateVisitNote = useMutation(
    ({ id, data }: { id: string; data: any }) =>
      executeUpdate({
        entityType: 'medicalRecord',
        table: 'medicalRecords',
        entityId: id,
        payload: data,
        networkFn: () => medicalRecordsApi.update(id, data),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['horse-records', horseId]);
        enqueueSnackbar('Visit note updated', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to update visit note', { variant: 'error' });
      },
    }
  );

  const handleVisitNoteSave = (data: { horseId: string; type: string; date: string; notes: string; tags: string[] }) => {
    if (editingVisitNote) {
      updateVisitNote.mutate({ id: editingVisitNote.id, data: { notes: data.notes, date: data.date, tags: data.tags } });
    } else {
      createVisitNote.mutate(data);
    }
    setVisitNoteDialogOpen(false);
    setEditingVisitNote(null);
  };

  const handleEditVisitNote = (record: MedicalRecord) => {
    setEditingVisitNote(record);
    setVisitNoteDialogOpen(true);
  };

  const deleteClientCommunication = useMutation(
    (id: string) =>
      executeDelete({
        entityType: 'clientCommunication',
        table: 'clientCommunications',
        entityId: id,
        networkFn: () => clientCommunicationService.delete(id),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client-communications', horseId]);
        enqueueSnackbar('Communication deleted', { variant: 'success' });
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to delete communication', { variant: 'error' });
      },
    }
  );

  const archiveSoapNote = useMutation(
    (id: string) =>
      executeUpdate({
        entityType: 'soapNote',
        table: 'soapNotes',
        entityId: id,
        payload: { isArchived: true },
        networkFn: () => soapNotesService.archive(id),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['soap-notes', horseId]);
        setAnchorPos(null);
        enqueueSnackbar('SOAP note archived', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to archive SOAP note', { variant: 'error' });
      },
    }
  );

  const archiveProcedure = useMutation(
    (id: string) =>
      executeUpdate({
        entityType: 'procedure',
        table: 'procedures',
        entityId: id,
        payload: { isArchived: true },
        networkFn: () => ProcedureService.archive(id),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['procedures', horseId]);
        setAnchorPos(null);
        enqueueSnackbar('Procedure archived', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to archive procedure', { variant: 'error' });
      },
    }
  );

  const archiveVaccination = useMutation(
    (id: string) =>
      executeUpdate({
        entityType: 'vaccination',
        table: 'vaccinations',
        entityId: id,
        payload: { isArchived: true },
        networkFn: () => VaccinationService.archive(id),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vaccinations', horseId]);
        queryClient.invalidateQueries(['horse-records', horseId]);
        setAnchorPos(null);
        enqueueSnackbar('Vaccination archived', { variant: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error.response?.data?.message || 'Failed to archive vaccination', { variant: 'error' });
      },
    }
  );

  // Sort records chronologically (most recent first for better UX)
  const sortedRecords = useMemo(() => {
    return [...records].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [records]);

  // Filter and sort procedures based on showArchived
  const filteredProceduresForAll = useMemo(() => {
    const filtered = showArchived 
      ? procedures.filter(proc => proc.isArchived) 
      : procedures.filter(proc => !proc.isArchived);
    // Sort by date descending (most recent first)
    return [...filtered].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [procedures, showArchived]);

  // Filter and sort medications based on showArchived
  const filteredMedicationsForAll = useMemo(() => {
    const filtered = showArchived 
      ? medications.filter(med => med.isArchived) 
      : medications.filter(med => !med.isArchived);
    // Sort by startDate descending (most recent first)
    return [...filtered].sort((a, b) => 
      new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
  }, [medications, showArchived]);

  // Filter vaccinations based on showArchived
  const filteredVaccinations = useMemo(() => {
    const filtered = showArchived
      ? allVaccinations.filter((v: any) => v.isArchived)
      : allVaccinations.filter((v: any) => !v.isArchived);
    return [...filtered].sort((a: any, b: any) =>
      new Date(b.dateAdministered).getTime() - new Date(a.dateAdministered).getTime()
    );
  }, [allVaccinations, showArchived]);

  // Group ALL medications by CALENDAR day for counting (includes archived).
  // The day key is the date PART of Medication.startDate (chartDates.ts) — the
  // old browser-zone key filed a UTC-midnight row under the previous day, and
  // this key is also the timeline row id (`medication-group-<day>`).
  const allGroupedMedications = useMemo(
    () => groupMedicationsByStartDay(medications as any[]),
    [medications],
  );

  // Group medications by CALENDAR day for display (filtered)
  const groupedMedications = useMemo(
    () => groupMedicationsByStartDay(filteredMedicationsForAll as any[]),
    [filteredMedicationsForAll],
  );

  // Combine and sort all clinical data
  const allClinicalData = useMemo(() => {
    const combined = [
      ...sortedRecords.filter(r => r.type !== 'medication' && r.type !== 'vaccination').map(record => ({ ...record, itemType: 'record' })),
      ...soapNotes.map(note => ({ ...note, itemType: 'soap', date: note.date })),
      ...filteredProceduresForAll.map(proc => ({ 
        ...proc, 
        itemType: 'procedure', 
        date: proc.date,
        type: 'procedure',
        id: proc.id
      })),
      ...groupedMedications.map(group => ({
        ...group,
        itemType: 'medication-group',
        // group.date is a 'yyyy-MM-dd' day key (used for the stable id below),
        // but for display/sort we want a real timestamp so the row shows the
        // actual time and the correct day. meds are sorted newest-first, so
        // medications[0].startDate is the representative instant for the group.
        date: group.medications?.[0]?.startDate ?? group.date,
        id: `medication-group-${group.date}`,
      })),
      ...filteredVaccinations.map((v: any) => ({
        ...v,
        itemType: 'vaccination',
        type: 'vaccination',
        date: v.dateAdministered,
      })),
      ...(dischargeSummaries as any[]).map((d: any) => ({
        ...d,
        itemType: 'discharge',
        date: d.dischargeDateTime || d.admissionDateTime || d.createdAt,
      })),
      ...stableDocsShares.map((s) => ({ ...s, itemType: 'stabledocs', date: s.sentAt })),
    ];

    return combined.sort((a, b) =>
      (safeDate(b.date)?.getTime() ?? 0) - (safeDate(a.date)?.getTime() ?? 0)
    );
  }, [sortedRecords, soapNotes, filteredProceduresForAll, groupedMedications, filteredVaccinations, dischargeSummaries, stableDocsShares]);

  // Group records by type for the tabbed view
  const recordsByType = useMemo(() => {
    return {
      all: allClinicalData,
      visitNote: sortedRecords.filter(r => r.type === 'visit_note' || r.type === 'Visit Note').map(r => ({ ...r, itemType: 'record' })),
      soap: soapNotes,
      medication: groupedMedications,
      procedure: filteredProceduresForAll.map(proc => ({
        ...proc,
        itemType: 'procedure',
        type: 'procedure',
        date: proc.date
      })),
      vaccination: filteredVaccinations.map((v: any) => ({
        ...v,
        itemType: 'vaccination',
        type: 'vaccination',
        date: v.dateAdministered,
      })),
    };
  }, [soapNotes, filteredProceduresForAll, groupedMedications, filteredVaccinations, allClinicalData, sortedRecords]);

  // Compute tag suggestions from existing visit notes (sorted by frequency, min 3 uses)
  const visitNoteTagSuggestions = useMemo(() => {
    const tagCounts = new Map<string, number>();
    records
      .filter(r => r.type === 'visit_note' && r.tags)
      .forEach(r => (r.tags as string[]).forEach(t => tagCounts.set(t, (tagCounts.get(t) || 0) + 1)));
    return Array.from(tagCounts.entries())
      .filter(([tag, count]) => tag !== 'Imported' && count >= 3)
      .sort((a, b) => b[1] - a[1])
      .map(([tag]) => tag);
  }, [records]);

  // Search, date range, and tag filter for clinical records
  const searchFilteredRecords = useMemo(() => {
    const term = clinicalSearchTerm.toLowerCase().trim();
    const fromDate = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
    const toDate = dateTo ? new Date(dateTo + 'T23:59:59') : null;
    const hasFilters = term || fromDate || toDate || selectedTagFilter;
    if (!hasFilters) return recordsByType;

    const matchesDate = (item: any): boolean => {
      if (!fromDate && !toDate) return true;
      const itemDate = safeDate(item.date || item.dateAdministered || item.startDate);
      if (!itemDate) return true; // no parseable date — don't filter it out
      if (fromDate && itemDate < fromDate) return false;
      if (toDate && itemDate > toDate) return false;
      return true;
    };

    const matchesTag = (item: any): boolean => {
      if (!selectedTagFilter) return true;
      const tags = item.tags as string[] | undefined;
      return tags ? tags.includes(selectedTagFilter) : false;
    };

    const matchesSearch = (item: any): boolean => {
      if (!term) return true;
      const fields = [
        item.notes, item.name, item.description, item.findings,
        item.vaccineName, item.instructions, item.type,
        item.subjective?.text || item.subjective?.notes || item.subjective?.aiSummary,
        item.objective?.text || item.objective?.notes || item.objective?.aiSummary,
        item.assessment?.text || item.assessment?.notes || item.assessment?.primaryDiagnosis,
        item.plan?.text || item.plan?.planNotes,
        item.templateUsed, item.recipientName, item.recipientEmail,
        // Also search tags
        ...(Array.isArray(item.tags) ? item.tags : []),
      ];
      return fields.some(f => f && String(f).toLowerCase().includes(term));
    };

    const matchesMedGroup = (group: any): boolean => {
      if (!matchesDate(group)) return false;
      if (!term) return true;
      if (!group.medications) return false;
      return group.medications.some((m: any) =>
        [m.name, m.instructions, m.dosage, m.notes].some(f => f && String(f).toLowerCase().includes(term))
      );
    };

    const filterItem = (item: any): boolean => {
      if (!matchesDate(item)) return false;
      if (!matchesTag(item)) return false;
      if (item.itemType === 'medication-group') return matchesMedGroup(item);
      return matchesSearch(item);
    };

    return {
      all: recordsByType.all.filter(filterItem),
      visitNote: recordsByType.visitNote.filter(item => matchesDate(item) && matchesTag(item) && matchesSearch(item)),
      soap: recordsByType.soap.filter(item => matchesDate(item) && matchesSearch(item)),
      medication: recordsByType.medication.filter(matchesMedGroup),
      procedure: recordsByType.procedure.filter(item => matchesDate(item) && matchesSearch(item)),
      vaccination: recordsByType.vaccination.filter(item => matchesDate(item) && matchesSearch(item)),
    };
  }, [recordsByType, clinicalSearchTerm, dateFrom, dateTo, selectedTagFilter]);

  // Get last visit date from past appointments
  const lastVisitDate = useMemo(() => {
    if (!horseId) return null;

    // The query is already horseId-scoped; pick the most recent past
    // appointment, excluding cancelled ones (a cancelled booking is not a
    // visit — matches the backend lastVisitAt and appointments conventions).
    const pastAppointments = horseAppointmentsForLastVisit
      .filter((apt: any) => apt.status !== 'cancelled' && new Date(apt.date) < new Date())
      .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());

    if (pastAppointments.length === 0) return null;

    return new Date(pastAppointments[0].date);
  }, [horseAppointmentsForLastVisit, horseId]);

  // Next upcoming appointment — surfaced in the context rail. Skip cancelled
  // bookings so a cancelled slot never shows as the next visit.
  const nextAppointmentDate = useMemo(() => {
    const upcoming = horseAppointmentsForLastVisit
      .filter((apt: any) => apt.status !== 'cancelled' && new Date(apt.date) >= new Date())
      .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
    return upcoming.length > 0 ? new Date(upcoming[0].date) : null;
  }, [horseAppointmentsForLastVisit]);

  // Draft SOAP — surfaces a "Resume" quick action so a vet who walked away
  // can pick up exactly where they left off without hunting through the
  // timeline.
  const draftSoap = useMemo<SoapNote | null>(() => {
    const drafts = (allSoapNotes as SoapNote[])
      .filter((n) => !n.isArchived && n.status === 'DRAFT' as any)
      .sort((a, b) => new Date(b.updatedAt || b.date).getTime() - new Date(a.updatedAt || a.date).getTime());
    return drafts[0] ?? null;
  }, [allSoapNotes]);

  // Vaccines past due, from the clinic's today — surfaces as a contextual
  // badge in the quick action strip.
  const overdueVaccineCount = useMemo(
    () => overdueVaccinationCount(allVaccinations as any[], clinicTz),
    [allVaccinations, clinicTz],
  );

  // Currently-active prescriptions — feeds the rail and the timeline kind counts.
  // A med is "active" when not archived AND either has no end date or its end
  // date is still in the future.
  const activeMedications = useMemo(() => {
    const now = Date.now();
    return (medications as any[])
      .filter((m: any) => !m.isArchived)
      .filter((m: any) => !m.endDate || new Date(m.endDate).getTime() >= now)
      .map((m: any) => ({
        id: m.id,
        name: m.name,
        dosage: m.dosage,
        frequency: m.frequency,
        endDate: m.endDate,
      }));
  }, [medications]);

  // Map the heterogeneous, already-search-filtered "all" list into the uniform
  // shape the timeline expects. Skip nulls so a stray malformed item can't
  // crash the row renderer.
  const timelineEvents = useMemo<TimelineEvent[]>(
    () =>
      searchFilteredRecords.all
        .map((item: any) => toTimelineEvent(item))
        .filter((e: any): e is TimelineEvent => e !== null),
    [searchFilteredRecords.all],
  );

  // Per-kind counts power the filter-chip badges. Computed off the unfiltered
  // (by kind) list so a chip's count never drops to zero just because that
  // kind is the currently-selected filter.
  const kindCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: timelineEvents.length,
      soap: 0,
      medication: 0,
      vaccination: 0,
      procedure: 0,
      visit_note: 0,
      discharge: 0,
    };
    timelineEvents.forEach((e) => {
      counts[e.kind] = (counts[e.kind] || 0) + 1;
    });
    return counts;
  }, [timelineEvents]);

  // Final list rendered into the timeline — applies the kind filter on top of
  // the existing text/date/tag filters.
  const filteredTimelineEvents = useMemo(() => {
    if (activeFilter === 'all' || activeFilter === 'lab' || activeFilter === 'problem' || activeFilter === 'recall' || activeFilter === 'communication' || activeFilter === 'invoice' || activeFilter === 'treatment_sheet' || activeFilter === 'files' || activeFilter === 'vaccination') {
      // Special-tab filters swap the timeline for a dedicated panel; the
      // event list isn't shown so just return the unfiltered set.
      return timelineEvents;
    }
    return timelineEvents.filter((e) => e.kind === activeFilter);
  }, [timelineEvents, activeFilter]);

  // Calculate age + formatted DOB (dateUtils handles the UTC-midnight day
  // shift and the 1900-01-01 import placeholder).
  const age = useMemo(() => computeAgeYears(horse?.dateOfBirth), [horse]);
  const dob = useMemo(
    () => (isDobKnown(horse?.dateOfBirth) ? formatDob(horse?.dateOfBirth) : null),
    [horse]
  );

  /**
   * The horse's CURRENT Coggins - a NEWEST-WINS union over the two sources.
   *
   * The twin of `HorseProfileClassic`'s `coggins` memo, deliberately identical
   * in rule so the two layouts cannot tell a clinician different things about
   * the same horse. See that memo for the full statement of why newest-wins is
   * the right ranking and why the classifier is shared with the backend.
   *
   *   LEGACY  a Coggins-named `Vaccination` -> `nextDueDate`, no number, no cert
   *   NEW     a Coggins `LabResult` -> `expiryDate` + accession + certificate
   *
   * `labResultsForCount` is already fetched above (shared react-query key, with
   * an offline replica fallback), so this adds no request.
   *
   * Both arms feed the SAME `cogginsExpiryLabel`, so Classic's badge and this
   * chip print the identical day string. A record whose expiry is null or
   * unusable gets `COGGINS_NO_EXPIRY_LABEL` - never a green "on file", which
   * would read as "fine" for a row nobody has dated, and never "No record",
   * which is the false answer this whole change exists to remove.
   */
  const coggins = useMemo<{
    label: string;
    isOverdue: boolean;
    state: CogginsExpiryState;
    accession: string | null;
    attachmentId: string | null;
  } | null>(() => {
    type Candidate = { when: string; expiry: string | null; accession: string | null; attachmentId: string | null; positive: boolean };
    const candidates: Candidate[] = [];

    for (const v of allVaccinations as any[]) {
      if (v.isArchived || !isCogginsName(v.vaccineName)) continue;
      candidates.push({
        when: v.dateAdministered,
        expiry: v.nextDueDate ?? null,
        accession: null,
        attachmentId: null,
        positive: false,
      });
    }
    for (const lr of labResultsForCount as any[]) {
      if (lr.horseId !== horseId) continue;
      if (!isCogginsLabResultRow(lr)) continue;
      candidates.push({
        when: lr.reportDate,
        expiry: lr.expiryDate ?? null,
        accession: lr.accessionNumber ?? null,
        attachmentId: lr.pdfAttachmentId ?? null,
        positive: isPositiveCogginsResult(lr.results),
      });
    }
    if (candidates.length === 0) return null;

    // Newest record for identity; newest STILL-VALID known expiry for status
    // (pickCogginsSummary, chartDates.ts) - a NULL-expiry IDEXX result must not
    // hide a dated vaccination's green chip.
    const winner = pickCogginsSummary(candidates, clinicTz);
    if (!winner) return null;
    // Vaccination.nextDueDate and LabResult.expiryDate are BOTH calendar days
    // (the latter a Postgres `date`, read back at UTC midnight), so one helper
    // renders both - it prints the date PART and never re-zones it.
    const { label, isOverdue, state } = cogginsHeaderStatus(winner, clinicTz);
    return { label, isOverdue, state, accession: winner.accession, attachmentId: winner.attachmentId };
  }, [allVaccinations, labResultsForCount, horseId, clinicTz]);

  const handleAddEntry = (type: string) => {
    if (type === 'soap') {
      setSelectedSoapNote(null);
      setSoapDialogOpen(true);
    } else if (type === 'visit-note') {
      setEditingVisitNote(null);
      setVisitNoteDialogOpen(true);
    } else if (type === 'medication') {
      setMedicationDialogOpen(true);
    } else if (type === 'procedure') {
      setProcedureDialogOpen(true);
    } else if (type === 'vaccination') {
      setVaccinationDialogOpen(true);
    }
  };

  const handleSoapNoteSave = (note: SoapNote) => {
    // Refresh the medical records list
    queryClient.invalidateQueries(['horse-records', horseId]);
    queryClient.invalidateQueries(['soap-notes', horseId]);
    queryClient.invalidateQueries(['medications', horseId]);
    queryClient.invalidateQueries(['low-stock-inventory']);
    setSoapDialogOpen(false);
    setSelectedSoapNote(null); // Clear selection
  };

  const handleViewSoapNote = (note: SoapNote) => {
    setSelectedSoapNote(note);
    setSoapDialogOpen(true);
  };

  const handleEditSoapNote = (note: SoapNote) => {
    setSelectedSoapNote(note);
    setSoapDialogOpen(true);
  };

  const handleMedicationSave = async (medications: any[]) => {
      try {
      // Create medications directly using the new API, collecting the
      // resulting records so the dialog can chain Save & Print on Rx rows.
      const created = [];
      for (const medication of medications) {
        // The exact data object the create endpoint receives. Passed to BOTH
        // executeWrite.payload (for the optimistic replica row when offline)
        // and networkFn (the unchanged online POST), so the two never diverge.
        const payload = {
          horseId: horseId!,
          name: medication.name,
          dosage: medication.dosage,
          frequency: medication.frequency,
          route: medication.route,
          duration: medication.duration,
          quantity: medication.quantity,
          instructions: medication.instructions,
          prescribedBy: medication.prescribedBy || profile?.name || user?.displayName || user?.email?.split('@')[0],
          startDate: medication.startDate || new Date().toISOString(),
          endDate: medication.endDate,
          notes: medication.notes,
          // Per-unit price from the priced catalog pick — keeps the Rx
          // auto-bill line item from being silently $0.
          unitPrice: medication.unitPrice,
          // DEA controlled-substance schedule; '' (not controlled) → undefined.
          deaSchedule: medication.deaSchedule || undefined,
          // Rx fields — backend defaults expirationDate to startDate + 12mo
          // when isRx=true and expirationDate is omitted, so passing undefined
          // (rather than null) here triggers that default. C-II hard-block
          // runs on the backend.
          isRx: medication.isRx || undefined,
          refillsAuthorized: medication.isRx ? (medication.refillsAuthorized ?? 0) : undefined,
          expirationDate: medication.isRx && medication.expirationDate
            ? medication.expirationDate
            : undefined,
          // Stock location the dispense deducts from (dialog's "Deduct stock
          // from"); undefined = Automatic — server infers (ADR-0013). Rides
          // the same payload offline: the sync push threads it and strips it
          // from the Medication row.
          dispenseLocationId: medication.dispenseLocationId,
          // Bottle for the DEA auto-log; undefined = server FIFO pick (ADR-0014).
          deaLotId: medication.deaLotId,
        };
        const result = await executeWrite({
          entityType: 'medication',
          table: 'medications',
          payload,
          networkFn: () => medicationService.create(payload),
        });
        created.push(result);
      }

      // Refresh queries
      queryClient.invalidateQueries(['medications', horseId]);
    queryClient.invalidateQueries(['low-stock-inventory']);
    queryClient.invalidateQueries(['horse-records', horseId]);
    enqueueSnackbar('Medications saved successfully', { variant: 'success' });
    setMedicationDialogOpen(false);
    return created;
    } catch (error: any) {
      // Offline write blocked — now only on out-of-storage (quota) or an
      // online-only action (e.g. invoices), NOT persist()-denial. Surface that
      // specific message instead of the generic save-failure one.
      const isBlocked =
        error instanceof OfflineWriteBlockedError ||
        error?.name === 'OfflineWriteBlockedError';
      enqueueSnackbar(
        isBlocked
          ? error.message
          : error?.response?.data?.message || 'Failed to save medications',
        { variant: 'error' },
      );
      // Re-throw so the dialog stays open + shows the inline error too
      throw error;
    }
  };

  const handleProcedureSave = async (data: { procedure: string; template: string; specifics: string; date: Date; performedBy: string }) => {
    try {
      if (editingProcedure) {
        // Update existing procedure
        const updateData = {
          name: data.procedure,
          description: data.specifics,
          category: data.template || undefined,
          date: data.date.toISOString(),
          performedBy: data.performedBy || undefined,
          notes: data.template ? `Template: ${data.template}` : undefined,
        };
        await executeUpdate({
          entityType: 'procedure',
          table: 'procedures',
          entityId: editingProcedure.id,
          payload: updateData,
          networkFn: () => ProcedureService.update(editingProcedure.id, updateData),
        });
      } else {
        // Create new procedure
        const createData = {
          horseId: horseId!,
          name: data.procedure,
          description: data.specifics,
          category: data.template || undefined,
          date: data.date.toISOString(),
          performedBy: data.performedBy || undefined,
          notes: data.template ? `Template: ${data.template}` : undefined,
        };
        // Offline-aware create: online (or flag off) this is byte-identical to
        // ProcedureService.create; offline it queues + optimistically writes the row.
        await executeWrite({
          entityType: 'procedure',
          table: 'procedures',
          payload: createData,
          networkFn: () => ProcedureService.create(createData),
        });
      }

      // Refresh the procedures list and all clinical data
      queryClient.invalidateQueries(['procedures', horseId]);
      queryClient.invalidateQueries(['horse-records', horseId]);
      setProcedureDialogOpen(false);
      setEditingProcedure(null);
    } catch (err: any) {
      console.error(`Failed to ${editingProcedure ? 'update' : 'create'} procedure:`, err);
      if (err instanceof OfflineWriteBlockedError || err?.name === 'OfflineWriteBlockedError') {
        // Offline write blocked (out-of-storage or online-only) — surface the real reason.
        enqueueSnackbar(err.message, { variant: 'error' });
      }
      throw err; // Re-throw so the dialog can display the error
    }
  };

  const handleEditProcedure = (procedure: any) => {
    setEditingProcedure(procedure);
    setProcedureDialogOpen(true);
  };

  const handleVaccinationSave = async (vaccinations: any[]) => {
    try {
      for (const vaccination of vaccinations) {
        const data = toCreateVaccinationDto(vaccination, horseId!);
        await executeWrite({
          entityType: 'vaccination',
          table: 'vaccinations',
          payload: data,
          networkFn: () => VaccinationService.create(data),
        });
      }
      queryClient.invalidateQueries(['vaccinations', horseId]);
      queryClient.invalidateQueries(['low-stock-inventory']);
      queryClient.invalidateQueries(['horse-records', horseId]);
      enqueueSnackbar('Vaccinations saved successfully', { variant: 'success' });
      setVaccinationDialogOpen(false);
    } catch (error: any) {
      if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
        enqueueSnackbar(error.message, { variant: 'error' });
        return;
      }
      enqueueSnackbar(
        error?.response?.data?.message || 'Failed to save vaccinations',
        { variant: 'error' },
      );
    }
  };

  const handleCloseSoapDialog = () => {
    setSoapDialogOpen(false);
    setSelectedSoapNote(null); // Clear selection when closing
  };

  const handleArchiveRecord = (id: string) => {
    setConfirmAction({
      message: 'Are you sure you want to archive this record? You can restore it later if needed.',
      onConfirm: () => archiveRecord.mutate(id),
    });
  };

  const handleArchiveSoapNote = (id: string) => {
    setConfirmAction({
      message: 'Are you sure you want to archive this SOAP note? You can restore it later if needed.',
      onConfirm: () => archiveSoapNote.mutate(id),
    });
  };

  const handleArchiveProcedure = (id: string) => {
    setConfirmAction({
      message: 'Are you sure you want to archive this procedure? You can restore it later if needed.',
      onConfirm: () => archiveProcedure.mutate(id),
    });
  };

  const handleArchiveVaccination = (id: string) => {
    setConfirmAction({
      message: 'Are you sure you want to archive this vaccination? You can restore it later if needed.',
      onConfirm: () => archiveVaccination.mutate(id),
    });
  };

  const handleSoapNoteMenuOpen = (event: React.MouseEvent<HTMLElement>, soapNoteId: string) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorPos({ top: rect.bottom, left: rect.right });
    setSelectedRecordId(soapNoteId);
  };

  const handleProcedureMenuOpen = (event: React.MouseEvent<HTMLElement>, procedureId: string) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorPos({ top: rect.bottom, left: rect.right });
    setSelectedRecordId(procedureId);
  };

  const handleVaccinationMenuOpen = (event: React.MouseEvent<HTMLElement>, vaccinationId: string) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorPos({ top: rect.bottom, left: rect.right });
    setSelectedRecordId(vaccinationId);
  };

  const handleRecordMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, recordId: string) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorPos({ top: rect.bottom, left: rect.right });
    setSelectedRecordId(recordId);
  };

  const handleRecordMenuClose = () => {
    setAnchorPos(null);
    setSelectedRecordId(null);
  };

  // Get medical alerts from horse data (now structured)
  const medicalAlerts = useMemo<Array<{ id: string; text: string; isCritical: boolean }>>(() => {
    const alerts = horse?.medicalAlerts || [];
    // Handle both old format (strings) and new format (objects)
    if (Array.isArray(alerts) && alerts.length > 0) {
      if (typeof alerts[0] === 'string') {
        // Old format - convert to display format
        return alerts.map((alert: any) => ({
          id: Math.random().toString(),
          text: alert,
          isCritical: false,
        }));
      }
      return alerts as unknown as Array<{ id: string; text: string; isCritical: boolean }>;
    }
    return [];
  }, [horse]);

  const handleGenerateInvoice = () => {
    if (!horseId) return;
    // Navigate to billing form with pre-populated horse data (consistent with appointments flow)
    navigate('/billing', { state: { horseId, autoImportHorse: true } });
  };

  const handleViewInvoice = () => {
    if (invoiceNotification.invoiceId) {
      navigate('/billing', { state: { invoiceId: invoiceNotification.invoiceId } });
    }
  };

  if (horseLoading || recordsLoading || soapNotesLoading || proceduresLoading) {
    return (
      <Container maxWidth="xl">
        <Box py={3}>
          <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={400} />
        </Box>
      </Container>
    );
  }

  if (horseError) {
    return (
      <Container maxWidth="xl">
        <Box py={3}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load patient data. Please try refreshing the page.
          </Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/patients')}>
            Back to Patients
          </Button>
        </Box>
      </Container>
    );
  }

  if (!horse) {
    return (
      <Container maxWidth="xl">
        <Box py={3} textAlign="center">
          <Typography variant="h5">Horse not found</Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/patients')}
            sx={{ mt: 2 }}
          >
            Back to Patients
          </Button>
        </Box>
      </Container>
    );
  }

  const deceased = (horse as any).status === 'DECEASED';

  return (
    <Container maxWidth={false} disableGutters sx={{ px: 0 }}>
      <Box>
        {/* Two-row sticky header — escapes parent padding via negative
            margins to span edge-to-edge. Row 1: identity + nav. Row 2: icon bar. */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            mx: { xs: -2, sm: -3 },
            mt: { xs: -2, sm: -3 },
            mb: 2,
            bgcolor: deceased ? 'rgba(241,239,232,0.97)' : 'rgba(250,249,246,0.95)',
            backdropFilter: 'blur(10px)',
            borderBottom: deceased ? '2px solid #B4B2A9' : 'none',
          }}
        >
          {/* Row 1 — identity & navigation */}
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.25,
              px: { xs: 2, sm: 3 }, py: 1.5, flexWrap: 'nowrap',
              borderBottom: '1px solid',
              borderColor: deceased ? '#B4B2A9' : 'rgba(139,94,52,0.15)',
            }}
          >
            <Tooltip title="Back to Patients" arrow>
              <IconButton size="small" onClick={() => navigate('/patients')} sx={{ flexShrink: 0 }}>
                <ArrowBackIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Tooltip>

            {/* Horseshoe icon in circular frame */}
            <Box
              sx={{
                width: 40, height: 40, borderRadius: '50%',
                bgcolor: deceased ? '#D3D1C7' : 'rgba(139,94,52,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, opacity: deceased ? 0.7 : 1,
              }}
            >
              <HoofIcon sx={{ fontSize: 22, color: deceased ? '#888780' : 'primary.main' }} />
            </Box>

            {/* Barn name */}
            <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.3rem' }, lineHeight: 1.2, flexShrink: 0, color: deceased ? '#444441' : 'text.primary' }}>
              {horse.name}
            </Typography>

            {/* Registered name */}
            {(horse as any).registeredName && (
              <Typography sx={{ fontSize: '0.95rem', fontStyle: 'italic', flexShrink: 0, color: deceased ? '#888780' : 'text.secondary' }}>
                {(horse as any).registeredName}
              </Typography>
            )}

            {/* Patient ID */}
            {(horse as any).patientId && (
              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.82rem', letterSpacing: 0.5, flexShrink: 0, color: deceased ? '#888780' : 'text.secondary' }}>
                {(horse as any).patientId}
              </Typography>
            )}

            {/* Status badge */}
            {((horse as any).status === 'ACTIVE' || !(horse as any).status) && (
              <Chip size="small" label="Active" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#2E7D32', color: '#fff', borderRadius: '999px', '& .MuiChip-label': { px: 1 } }} />
            )}
            {(horse as any).status === 'INACTIVE' && (
              <Chip size="small" label="Inactive" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }} />
            )}
            {deceased && (
              <Chip size="small" label="Deceased" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#7F1D1D', color: '#fff', borderRadius: '999px', '& .MuiChip-label': { px: 1 } }} />
            )}

            {/* Breed · Sex · Age · Color */}
            <Typography
              data-testid="patient-identity-line"
              sx={{ fontSize: '0.88rem', color: deceased ? '#888780' : 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 1, minWidth: 0 }}
            >
              {[formatBreed(horse), horse.gender, age !== null ? `${age}y${dob ? ` (${dob})` : ''}` : null, horse.color].filter(Boolean).join(' · ')}
            </Typography>

            {deceased && (
              <Chip size="small" label="Read only" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#E8E6DF', color: '#5F5E5A', borderRadius: '999px', flexShrink: 0, '& .MuiChip-label': { px: 1 } }} />
            )}

            {/* Location */}
            {horse.location && (
              <Typography
                sx={{ fontSize: '0.88rem', color: deceased ? '#888780' : 'text.secondary', display: 'inline-flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}
              >
                <PlaceIcon sx={{ fontSize: 15 }} /> {horse.location}
              </Typography>
            )}

            <Box sx={{ flexGrow: 1 }} />
            {/* P6: mounted-and-disabled while the permission read is in flight, absent
                once it has ANSWERED without manage:patients. ⚠ The <span> is not
                decoration — a disabled MUI control takes no pointer events, so a
                Tooltip wrapping one never fires without it (the wrapper
                pages/Settings/UserManagement.tsx already uses on its disabled row
                actions). */}
            {(canEdit || permissionsPending) && (
              <Tooltip title="Edit patient" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setEditPatientOpen(true)}
                    data-testid="edit-patient-button"
                    disabled={!canEdit}
                    sx={{ color: 'text.secondary', flexShrink: 0 }}
                  >
                    <EditIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <ChartLayoutToggle layout={layout} onChange={setLayout} />

          </Box>

          {/* Row 2 — action icons */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: { xs: 2, sm: 3 },
              py: 0.5,
              borderBottom: '1px solid',
              borderColor: deceased ? '#B4B2A9' : 'rgba(139,94,52,0.15)',
            }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Tooltip title="Patient identity details" arrow>
                <Box onClick={(e) => setIdentityAnchor(e.currentTarget)} data-testid="identity-info-btn" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, px: 0.75, py: 0.5, borderRadius: 1, cursor: 'pointer', color: 'text.secondary', '&:hover': { bgcolor: 'rgba(139,94,52,0.06)' } }}>
                  <InfoIcon sx={{ fontSize: 16 }} />
                  <Typography sx={{ fontSize: '0.65rem', lineHeight: 1 }}>Details</Typography>
                </Box>
              </Tooltip>
              <Tooltip title="Owner & trainer details" arrow>
                <Box onClick={(e) => setOwnerAnchor(e.currentTarget)} data-testid="owner-info-btn" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, px: 0.75, py: 0.5, borderRadius: 1, cursor: 'pointer', color: 'text.secondary', '&:hover': { bgcolor: 'rgba(139,94,52,0.06)' } }}>
                  <PeopleIcon sx={{ fontSize: 16 }} />
                  <Typography sx={{ fontSize: '0.65rem', lineHeight: 1 }}>Client info</Typography>
                </Box>
              </Tooltip>
              {horse.owners && horse.owners.length > 0 && horse.owners[0].email && (
                <Tooltip title={`Email ${horse.owners[0].name}: ${horse.owners[0].email}`} arrow>
                  <Box component="a" href={`mailto:${horse.owners[0].email}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, px: 0.75, py: 0.5, borderRadius: 1, cursor: 'pointer', color: 'text.secondary', textDecoration: 'none', '&:hover': { bgcolor: 'rgba(139,94,52,0.06)' } }}>
                    <EmailIcon sx={{ fontSize: 16 }} />
                    <Typography sx={{ fontSize: '0.65rem', lineHeight: 1 }}>Email</Typography>
                  </Box>
                </Tooltip>
              )}
            </Stack>
          </Box>
        </Box>

        {/* Unallocated result suggestions — silent unless an Inbox item
            looks like it belongs to this patient. One-click jumps to triage. */}
        {horse && panels.labs && <UnallocatedSuggestionBanner horse={horse} />}

        {/* Action-first quick strip — anticipates the vet's next move:
            resume the draft if one exists, otherwise start a new SOAP. */}
        <QuickActionStrip
          draftSoap={draftSoap}
          overdueVaccineCount={overdueVaccineCount}
          pendingRefillCount={0}
          canCreateSoap={canCreateSoapNote}
          canEdit={canEdit}
          canAddClinical={canAddClinical}
          onResumeDraft={(n) => { setSelectedSoapNote(n); setSoapDialogOpen(true); }}
          onNewSoap={() => handleAddEntry('soap')}
          onNewVisitNote={() => handleAddEntry('visit-note')}
          onAddMedication={() => handleAddEntry('medication')}
          onAddVaccination={() => handleAddEntry('vaccination')}
          onAddProcedure={() => handleAddEntry('procedure')}
          onLogCommunication={() => setClientCommDialogOpen(true)}
        />

        {/* Familiar pill-style filter chips with subtle type-color accent
            on the active state. Replaces the original flat chip bar with
            slightly nicer affordances but keeps the shape vets know. */}
        <Box sx={{ mt: 1, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            <FilterChips
              active={activeFilter}
              counts={{
                ...kindCounts,
                lab: labsCount,
                problem: problemsForCount.length,
                recall: recallsForCount.length,
                communication: clientCommunications.length,
                treatment_sheet: (horseTreatmentSheets as any[]).length,
                files: filesCount,
              }}
              onSelect={setActiveFilter}
            />
          </Box>
          <Button
            size="small"
            startIcon={<ArchiveIcon sx={{ fontSize: 14 }} />}
            variant={showArchived ? 'contained' : 'text'}
            onClick={() => setShowArchived(!showArchived)}
            sx={{ fontSize: '0.72rem', textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {showArchived ? 'Showing archived' : 'Show archived'}
          </Button>
          {/* Disabled (not hidden) while the permission read is in flight, like Edit patient. The PDF
              is built and emailed on the server, so it is also disabled offline (INV-19). */}
          {can.shareRecords && (
            <Tooltip title={isOffline ? 'Sharing records needs a connection' : ''}>
              <span style={{ flexShrink: 0 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ShareIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setShareOpen(true)}
                  data-testid="share-records-btn"
                  disabled={permissionsPending || isOffline}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'text.secondary', bgcolor: 'transparent' } }}
                >
                  Share Records
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>

        <Grid container spacing={1.5}>
          {/* Left rail — sticks while the chart scrolls so allergies +
              active meds stay one glance away. */}
          <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box
              sx={{
                position: 'sticky',
                top: 64,
                maxHeight: 'calc(100vh - 80px)',
                overflowY: 'auto',
                pr: 0.5,
              }}
            >
            <ContextRail
              alerts={medicalAlerts}
              activeMedications={activeMedications}
              activeProblems={problemsForCount as any[]}
              activeRecalls={recallsForCount as any[]}
              lastVisitDate={lastVisitDate}
              nextAppointmentDate={nextAppointmentDate}
              canEdit={can.manageAlerts}
              onEditAlerts={() => setAlertsDialogOpen(true)}
              onShowActiveMeds={() => setActiveFilter('medication')}
              onShowProblems={() => setActiveFilter('problem')}
              onShowRecalls={() => setActiveFilter('recall')}
            />
            </Box>
          </Grid>

          {/* Right — timeline OR a swap-in panel for labs / problems / recalls / comms */}
          <Grid item xs={12} md={9}>
            <Paper sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              {/* Search / date filter row — hidden when a swap panel is showing
                  since those panels own their own filters. */}
              {!(activeFilter === 'lab' || activeFilter === 'problem' || activeFilter === 'recall' || activeFilter === 'communication' || activeFilter === 'invoice' || activeFilter === 'treatment_sheet' || activeFilter === 'files' || activeFilter === 'vaccination') && (
                <>
                  <Box sx={{ px: 1.75, pt: 1.5, pb: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="Search chart…"
                      value={clinicalSearchTerm}
                      onChange={(e) => setClinicalSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ flex: 1, minWidth: 140, '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="From"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 145, '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="To"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 145, '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
                    />
                    {(clinicalSearchTerm || dateFrom || dateTo || selectedTagFilter) && (
                      <Button
                        size="small"
                        onClick={() => { setClinicalSearchTerm(''); setDateFrom(''); setDateTo(''); setSelectedTagFilter(null); }}
                        sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                  {visitNoteTagSuggestions.length > 0 && (activeFilter === 'all' || activeFilter === 'visit_note') && (
                    <Box sx={{ px: 1.75, pb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Tags:</Typography>
                      {visitNoteTagSuggestions.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          variant={selectedTagFilter === tag ? 'filled' : 'outlined'}
                          color={selectedTagFilter === tag ? 'primary' : 'default'}
                          onClick={() => setSelectedTagFilter(selectedTagFilter === tag ? null : tag)}
                          sx={{ fontSize: '0.7rem', height: 22, cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  )}
                  <Divider />
                </>
              )}

              <Box sx={{ minHeight: 280 }} data-testid="chart-body">
                {/* THE REFUSALS, one per chip, each in the place its panel would have been.
                    `panels.X` is false only on a KNOWN answer that lacks the slug the panel's
                    list read declares, so on every non-answer these render nothing and the
                    panels below render exactly as they always did. One sentence, from
                    `noAccessMessage`, so the chart says what every other refused surface says. */}
                {activeFilter === 'lab' && !panels.labs && (
                  <Box sx={{ p: 2 }} data-testid="chart-no-access-lab">
                    <NoAccess dense message={noAccessMessage(CHART_PANELS.labs.noun)} />
                  </Box>
                )}
                {activeFilter === 'lab' && panels.labs && horseId && (
                  <Box sx={{ p: 2 }}>
                    <LabsDiagnosticsPanel horseId={horseId} />
                  </Box>
                )}
                {activeFilter === 'problem' && !panels.problems && (
                  <Box sx={{ p: 2 }} data-testid="chart-no-access-problem">
                    <NoAccess dense message={noAccessMessage(CHART_PANELS.problems.noun)} />
                  </Box>
                )}
                {activeFilter === 'problem' && panels.problems && horseId && (
                  <Box sx={{ p: 2 }}>
                    <ProblemListPanel horseId={horseId} />
                  </Box>
                )}
                {activeFilter === 'recall' && !panels.recalls && (
                  <Box sx={{ p: 2 }} data-testid="chart-no-access-recall">
                    <NoAccess dense message={noAccessMessage(CHART_PANELS.recalls.noun)} />
                  </Box>
                )}
                {activeFilter === 'recall' && panels.recalls && horseId && (
                  <Box sx={{ p: 2 }}>
                    <RecallRemindersPanel horseId={horseId} />
                  </Box>
                )}
                {activeFilter === 'vaccination' && !panels.vaccinations && (
                  <Box sx={{ p: 2 }} data-testid="chart-no-access-vaccinations">
                    <NoAccess dense message={noAccessMessage(CHART_PANELS.vaccinations.noun)} />
                  </Box>
                )}
                {activeFilter === 'vaccination' && panels.vaccinations && (
                  <Box sx={{ p: 2 }}>
                    <PatientVaccinationsTab
                      vaccinations={filteredVaccinations}
                      allVaccinations={allVaccinations as any[]}
                      catalogNames={vaccinationCatalog.filter((i) => i.isActive && i.clinicId).map((i) => i.name)}
                      attachments={recordAttachmentMap}
                      onAdd={canAddClinical ? () => handleAddEntry('vaccination') : undefined}
                      onEdit={can.editClinicalRecord ? setEditingVaccination : undefined}
                      onMenu={canArchive ? handleVaccinationMenuOpen : undefined}
                    />
                  </Box>
                )}
                {activeFilter === 'files' && !panels.files && (
                  <Box sx={{ p: 2 }} data-testid="chart-no-access-files">
                    <NoAccess dense message={noAccessMessage(CHART_PANELS.files.noun)} />
                  </Box>
                )}
                {activeFilter === 'files' && panels.files && horseId && (
                  <PatientFilesTab horseId={horseId} onOpenSoapNote={handleViewSoapNote} />
                )}
                {activeFilter === 'communication' && !panels.communications && (
                  <Box sx={{ p: 2 }} data-testid="chart-no-access-communication">
                    <NoAccess dense message={noAccessMessage(CHART_PANELS.communications.noun)} />
                  </Box>
                )}
                {activeFilter === 'communication' && panels.communications && (
                  <Box sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {clientCommunications.length} communication{clientCommunications.length !== 1 ? 's' : ''}
                      </Typography>
                      {/* POST /client-communications → manage:medical_records (:27). */}
                      {can.addClinicalRecord && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => setClientCommDialogOpen(true)}
                        >
                          Log Communication
                        </Button>
                      )}
                    </Box>
                    {/* ⚠ `editClinicalRecord`, NOT `removeClinicalRecord`. DELETE
                        /client-communications/:id declares `manage:medical_records` ALONE
                        (client-communications.controller.ts:60), where the med / vaccination /
                        procedure deletes declare that slug AND the archive one. The family does
                        not agree with itself, so each control asks its own handler. The list
                        renders its row Delete only when it is given a handler, so the gate is
                        the prop. */}
                    <ClientCommunicationList
                      communications={clientCommunications}
                      onDelete={can.editClinicalRecord ? (id) => {
                        setConfirmAction({
                          message: 'Are you sure you want to delete this communication log?',
                          onConfirm: () => deleteClientCommunication.mutate(id),
                        });
                      } : undefined}
                    />
                  </Box>
                )}
                {activeFilter === 'discharge' && !panels.dischargeSummaries && (
                  <Box sx={{ p: 2 }} data-testid="chart-no-access-discharge">
                    <NoAccess dense message={noAccessMessage(CHART_PANELS.dischargeSummaries.noun)} />
                  </Box>
                )}
                {activeFilter === 'discharge' && panels.dischargeSummaries && (
                  <Box sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {(dischargeSummaries as any[]).length} discharge summar{(dischargeSummaries as any[]).length !== 1 ? 'ies' : 'y'}
                      </Typography>
                      {/* POST /discharge-summaries → manage:medical_records (:42). */}
                      {can.dischargeWrite && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => { setSelectedRecordId(null); setDischargeEditMode(false); setShowDischargeDialog(true); }}
                        >
                          New Discharge
                        </Button>
                      )}
                    </Box>
                    {(dischargeSummaries as any[]).length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No discharge summaries yet
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {(dischargeSummaries as any[])
                          .slice()
                          .sort((a: any, b: any) =>
                            new Date(b.dischargeDateTime || b.admissionDateTime || b.createdAt).getTime() -
                            new Date(a.dischargeDateTime || a.admissionDateTime || a.createdAt).getTime()
                          )
                          .map((d: any) => (
                            <Paper
                              key={d.id}
                              variant="outlined"
                              sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                              onClick={() => { setSelectedRecordId(d.id); setDischargeEditMode(false); setShowDischargeDialog(true); }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>
                                    {d.status === 'DRAFT' ? 'Discharge Summary (Draft)' : 'Discharge Summary'}
                                  </Typography>
                                  {d.attendingVet && (
                                    <Typography variant="caption" color="text.secondary">
                                      Vet: {d.attendingVet}
                                    </Typography>
                                  )}
                                </Box>
                                <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 2 }}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {/* DischargeSummary.dischargeDateTime / admissionDateTime are INSTANTS
                                        (DateTimePicker + `new Date()` on finalize) — the clinic zone's day. */}
                                    {d.dischargeDateTime
                                      ? formatInstant(d.dischargeDateTime, INSTANT_FORMATS.date)
                                      : d.admissionDateTime
                                      ? `Admitted ${formatInstant(d.admissionDateTime, INSTANT_FORMATS.date)}`
                                      : '—'}
                                  </Typography>
                                  <Chip
                                    label={d.status}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: '0.65rem',
                                      bgcolor: d.status === 'FINALIZED' ? '#E8F5E9' : '#FFF8E1',
                                      color: d.status === 'FINALIZED' ? '#2E7D32' : '#F57F17',
                                    }}
                                  />
                                </Box>
                              </Box>
                            </Paper>
                          ))}
                      </Box>
                    )}
                  </Box>
                )}
                {activeFilter === 'treatment_sheet' && !panels.treatmentSheets && (
                  <Box sx={{ p: 2 }} data-testid="chart-no-access-treatment-sheet">
                    <NoAccess dense message={noAccessMessage(CHART_PANELS.treatmentSheets.noun)} />
                  </Box>
                )}
                {activeFilter === 'treatment_sheet' && panels.treatmentSheets && (
                  <Box sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {(horseTreatmentSheets as any[]).length} treatment sheet{(horseTreatmentSheets as any[]).length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    {(horseTreatmentSheets as any[]).length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No treatment sheets for this patient
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {(horseTreatmentSheets as any[])
                          .slice()
                          .sort((a: any, b: any) => new Date(b.admitDate).getTime() - new Date(a.admitDate).getTime())
                          .map((ts: any) => (
                            <Paper
                              key={ts.id}
                              variant="outlined"
                              sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                              onClick={() => navigate(`/hospital/treatment-sheets/${ts.id}`)}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>
                                    {ts.chiefComplaint || 'Treatment Sheet'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {ts.attendingVet?.name ? `Vet: ${ts.attendingVet.name}` : ''}
                                    {ts.stall?.name ? ` · ${ts.stall.name}` : ts.locationLabel ? ` · ${ts.locationLabel}` : ''}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 2 }}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {/* TreatmentSheet.admitDate (`@default(now())`) and dischargeDate
                                        (`new Date()` / a posted instant) are INSTANTS — clinic zone. */}
                                    {formatInstant(ts.admitDate, INSTANT_FORMATS.date)}
                                    {ts.dischargeDate ? ` – ${formatInstant(ts.dischargeDate, INSTANT_FORMATS.date)}` : ''}
                                  </Typography>
                                  <Chip
                                    label={ts.status.charAt(0) + ts.status.slice(1).toLowerCase()}
                                    size="small"
                                    color={ts.status === 'ACTIVE' ? 'success' : 'default'}
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                </Box>
                              </Box>
                            </Paper>
                          ))}
                      </Box>
                    )}
                  </Box>
                )}
                {/* P6: the panel below rides view:billing, which is false for the whole
                    in-flight window of the /users/me read — so this Skeleton stands in for
                    it rather than rendering an empty invoice list to someone who has
                    invoices. It disappears the moment the read answers, in either
                    direction. Page regions get a block Skeleton; the CircularProgress
                    convention is for inside a dialog. */}
                {permissionsPending && activeFilter === 'invoice' && (
                  <Box sx={{ p: 2 }}>
                    <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1 }} />
                  </Box>
                )}
                {canViewBilling && !permissionsPending && activeFilter === 'invoice' && (
                  <Box sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {horseInvoiceStats?.totalInvoiceCount ?? 0} invoice{(horseInvoiceStats?.totalInvoiceCount ?? 0) !== 1 ? 's' : ''}
                        {(horseInvoiceStats?.balance || 0) > 0 && (
                          <Typography component="span" sx={{ ml: 1, color: 'error.main', fontWeight: 600 }}>
                            · {formatDollars(horseInvoiceStats?.balance ?? 0)} outstanding
                          </Typography>
                        )}
                        {/* A5c — a SIBLING of the balance clause above, never a child of it.
                            This layout renders no PatientHeaderCard (the card is Classic's),
                            so this line is the WHOLE in-flight surface on the Modern chart,
                            and the balance it explains can be $0 precisely BECAUSE an ACH is
                            clearing — the largest fall there is, and the one nesting would
                            hide. Money through money.ts (.claude/rules/frontend.md): a raw
                            toFixed drops the thousands separator, so the same balance would
                            print "$2712.50" here and "$2,712.50" on the card. */}
                        {(horseInvoiceStats?.processingAmount ?? 0) > 0 && (
                          <Typography component="span" sx={{ ml: 0.75, color: 'text.secondary', fontWeight: 400, fontSize: '0.8rem' }}>
                            ({formatDollars(horseInvoiceStats?.processingAmount ?? 0)} in flight, already deducted)
                          </Typography>
                        )}
                      </Typography>
                      {/* The WRITE half of this panel, and the only consumer of
                          manage:charges on this layout. Absent — not disabled — once the
                          read has answered without it: the panel around it is already
                          on screen, so nothing blinks and there is no in-flight window
                          left for this button to cover. */}
                      {canManageBilling && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={handleGenerateInvoice}
                        >
                          New Invoice
                        </Button>
                      )}
                    </Box>
                    {!horseInvoiceList?.data?.length ? (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No invoices yet
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {(horseInvoiceList.data as any[]).map((inv: any) => (
                          <Paper
                            key={inv.id}
                            data-testid="horse-invoice-row"
                            variant="outlined"
                            sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                            onClick={() => navigate('/billing', { state: { invoiceId: inv.id } })}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  Invoice #{inv.invoiceNumber || inv.id.slice(0, 8)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" data-testid="horse-invoice-date">
                                  {/* Invoice has no `date` scalar, so `inv.date` was dead and this
                                      caption always read '—'. Classic's twin shows Invoice.issuedAt
                                      (`@default(now())`, an INSTANT) — aligned, in the clinic zone. */}
                                  {formatInstant(inv.issuedAt || inv.createdAt, INSTANT_FORMATS.date) || '—'}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 2 }}>
                                <Typography variant="body2" fontWeight={600}>
                                  ${Number(inv.total ?? inv.amount ?? 0).toFixed(2)}
                                </Typography>
                                <Chip
                                  label={inv.status}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    bgcolor: inv.status === 'PAID' ? '#E8F5E9' : inv.status === 'OVERDUE' ? '#FFEBEE' : '#FFF8E1',
                                    color: inv.status === 'PAID' ? '#2E7D32' : inv.status === 'OVERDUE' ? '#C62828' : '#F57F17',
                                  }}
                                />
                              </Box>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
                {/* ...and the refusal, once the read has ANSWERED without view:billing.
                    The Invoices chip is a fixed list in components/FilterChips.tsx, so it
                    is offered to everyone: without this arm the chip led to a completely
                    blank region — the chart reporting nothing at all where it had reported
                    invoices the day before. `permissionsPending` is excluded so this never
                    races the Skeleton above it.

                    ⚠ `permissionsUnavailable` is excluded for a SEPARATE reason, and it is
                    the clause that looks redundant: this sentence asserts what the API
                    would answer, and that flag is the state where the API never answered.
                    Offline with no cached claim set the hook publishes `unavailable` for
                    this uid (useRBAC.ts:217-222), which clears `permissionsPending` — the
                    store now holds an entry for this user — while `permissions` stays null
                    and every flag here fails closed. Shown then, the sentence would tell a
                    barn device on a dead uplink that it lacks an access it may well hold,
                    and send the clinic off to grant a permission it already granted. The
                    region renders nothing in that state; the shell's
                    PermissionsUnavailableBanner (App.tsx:486) is what explains it, and it
                    is on screen directly above this one.

                    So the sentence below is reached only when the read ANSWERED without
                    view:billing — and it is then true of the API too: GET /billing refuses
                    the read itself, whatever else the user holds. (The editor will not
                    normally produce that state alone: REQUIRES_VIEW ties manage:charges to
                    view:billing in the UI's ceiling. That rule lives in the editor, not in
                    the PUT, which is why this arm reads the slug the READ gate declares
                    rather than inferring it from the write slug.) */}
                {!canViewBilling && !permissionsPending && activeFilter === 'invoice' && (
                  <Box sx={{ p: 2 }} data-testid="chart-no-access-invoice">
                    {/* ⚠ THE SENTENCE IS NOW THE SHARED ONE, and the two bespoke lines that stood
                        here are gone. They said the same thing in different words — "You do not
                        have access to invoices for this patient" / "Ask an owner or admin if you
                        need View billing" — which named the SLUG rather than the switch and left
                        this one panel reading like a different product from every other refusal
                        in the app. `noAccessMessage` names what an owner clicks.

                        ⚠ AND `!permissionsUnavailable` IS GONE ON PURPOSE. That clause was doing
                        the job `controlAnswerKnown` now does properly, and doing it too bluntly:
                        the bare flag is routinely true for a TRANSIENT failure that KEPT a
                        perfectly good answer (`mePermissions.publishPermissionsUnavailable`), so
                        it suppressed the sentence for people who really had been narrowed. The
                        predicate inside the map is the flag AND an empty set, which is the state
                        this clause was actually aiming at — an offline device with no cached
                        claims, where the panel renders nothing and the shell's banner explains. */}
                    <NoAccess dense message={noAccessMessage(CHART_PANELS.invoices.noun)} />
                  </Box>
                )}
                {activeFilter !== 'lab' && activeFilter !== 'problem' && activeFilter !== 'recall' && activeFilter !== 'communication' && activeFilter !== 'discharge' && activeFilter !== 'invoice' && activeFilter !== 'treatment_sheet' && activeFilter !== 'files' && activeFilter !== 'vaccination' && (
                  /* ⚠ THE THIRD STAGING DEFECT LIVED IN THE `length === 0` ARM BELOW. Every feed
                     the timeline merges — records, SOAP notes, medications, vaccinations,
                     procedures — is a `view:medical_records` read, so a refused person got zero
                     events and the empty state said "No chart entries yet", with an instruction
                     to use Add. That is a claim about the PATIENT ("this horse has no records")
                     made to somebody who simply may not look, standing in front of an owner who
                     knows the horse has a chart. The refusal has to come FIRST, because from
                     inside the arm an empty list and a forbidden list are the same array. */
                  !panels.medicalRecords ? (
                    <Box sx={{ p: 2 }} data-testid="chart-no-access-timeline">
                      <NoAccess dense message={noAccessMessage(CHART_PANELS.medicalRecords.noun)} />
                    </Box>
                  ) : filteredTimelineEvents.length === 0 ? (
                    <Box sx={{ py: 8, textAlign: 'center' }}>
                      <TimelineIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {/* The empty state keeps its TEXT and loses its call to action for a
                            person who cannot add: "use Add above" points at buttons that are no
                            longer on the strip. */}
                        {activeFilter === 'all'
                          ? (can.addClinicalRecord
                              ? 'No chart entries yet — use Add above to record one'
                              : 'No chart entries yet')
                          : 'No entries match this filter'}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ pt: 0.5, pb: 1, pr: 1.5 }}>
                      <TimelineSpine
                        events={filteredTimelineEvents}
                        /* ⚠ `editSoapNote`, NOT `canEdit`. Every row this spine renders is a
                           CLINICAL record — a SOAP note, a procedure, a visit note, a discharge
                           — and each Edit opens a form that PUTs to a `manage:medical_records`
                           route. It was wired to `canEdit` (`manage:patients`), so a CSR met a
                           pencil on every clinical row whose save 403s, and a technician holding
                           the clinical slug without the patient one met none at all. */
                        canEdit={can.editSoapNote}
                        canArchive={canArchive}
                        attachmentCountByNoteId={soapAttachmentCounts}
                        onSoapView={handleViewSoapNote}
                        onSoapEdit={handleEditSoapNote}
                        onProcedureEdit={handleEditProcedure}
                        onRecordEdit={handleEditVisitNote}
                        onDischargeEdit={(e) => { setSelectedRecordId(e.id); setDischargeEditMode(true); setShowDischargeDialog(true); }}
                        onOpenFull={(e) => setSelectedEvent(e)}
                        onMenu={canArchive ? (e, anchor) => {
                          setSelectedRecordId(e.id);
                          setAnchorPos(anchor);
                        } : undefined}
                      />
                    </Box>
                  )
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Mobile-only rail (folds below the chart on narrow screens) */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 1.5 }}>
          <ContextRail
            alerts={medicalAlerts}
            activeMedications={activeMedications}
            activeProblems={problemsForCount as any[]}
            activeRecalls={recallsForCount as any[]}
            lastVisitDate={lastVisitDate}
            nextAppointmentDate={nextAppointmentDate}
            canEdit={can.manageAlerts}
            onEditAlerts={() => setAlertsDialogOpen(true)}
            onShowActiveMeds={() => setActiveFilter('medication')}
            onShowProblems={() => setActiveFilter('problem')}
            onShowRecalls={() => setActiveFilter('recall')}
          />
        </Box>

        {/* Edit Patient — shared form dialog; saving invalidates ['horse', id]
            so the banner refreshes in place. */}
        <HorseFormDialog
          open={editPatientOpen}
          horse={horse ?? null}
          onClose={() => setEditPatientOpen(false)}
        />

        {/* Patient details popover — color/microchip/weight/etc behind a
            tap so the sticky banner stays slim. Vets rarely need these mid-visit. */}
        <Popover
          open={Boolean(identityAnchor)}
          anchorEl={identityAnchor}
          onClose={() => setIdentityAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { mt: 1, p: 2, minWidth: 280, borderRadius: 2 } } }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Patient details</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Date of Birth</Typography>
              <Typography variant="body2">{dob ?? 'Not on file'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Color</Typography>
              <Typography variant="body2">{horse.color || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Microchip</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{horse.microchipId || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Registration</Typography>
              <Typography variant="body2">{(horse as any).registrationNumber || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Weight</Typography>
              <Typography variant="body2">{horse.weight ? `${horse.weight} kg` : '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Height</Typography>
              <Typography variant="body2">{horse.height ? `${horse.height} hh` : '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Discipline</Typography>
              <Typography variant="body2">{(horse as any).discipline || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Location</Typography>
              <Typography variant="body2">{horse.location || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>PEI Number</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{(horse as any).peiNumber || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Coggins / EIA</Typography>
              {coggins ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                  {/* THE NUMBER. This literal 'EIA' was the SECOND hardcoded copy
                    * of the same non-fact - PatientHeaderCard printed it as the
                    * fallback of a prop nobody passed, and this layout, which has
                    * no PatientHeaderCard, simply typed it. Only a LabResult
                    * carries an accession; a legacy vaccination-path Coggins has
                    * none and still degrades to 'EIA'. */}
                  <Typography variant="body2" data-testid="coggins-accession" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{coggins.accession || 'EIA'}</Typography>
                  {/* Five states, the SAME five PatientHeaderCard's badge renders
                    * (Classic's pale palette, this layout's solid one). It used to
                    * be `isOverdue ? red : green`, which has no way to express
                    * "on file, expiry unknown" - so every undated Coggins, i.e.
                    * every row predating this feature (the column is nullable with
                    * no backfill), was painted GREEN and read as fine. Grey is not
                    * a softer green here; it is a different statement, and it is
                    * also distinct from the orange "No record" below. */}
                  <Chip data-testid="coggins-status-chip" label={coggins.label} size="small" sx={{ height: 18, fontSize: '0.67rem', borderRadius: '999px', bgcolor: COGGINS_CHIP_BG[coggins.state], color: '#fff', '& .MuiChip-label': { px: 0.75 } }} />
                  {/* The certificate, one click from the identity panel - same
                    * affordance Classic's header gained, because a negative
                    * Coggins is a document staff are asked to PRODUCE. */}
                  {coggins.attachmentId && (
                    <Tooltip title="Open the Coggins certificate">
                      <IconButton
                        size="small"
                        onClick={async () => {
                          // Awaited + caught: an un-awaited call here turns a
                          // failed fetch into an unhandled promise rejection.
                          try {
                            await downloadAuthedBlob(
                              `/attachments/${coggins.attachmentId}/download`,
                              `coggins-${horse.name}.pdf`,
                            );
                          } catch {
                            enqueueSnackbar('Failed to open the Coggins certificate', {
                              variant: 'error',
                            });
                          }
                        }}
                        data-testid="coggins-certificate-link"
                        sx={{ p: 0.25 }}
                      >
                        <CertificateIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ) : (
                <Chip label="No record" size="small" sx={{ height: 18, fontSize: '0.67rem', bgcolor: '#E65100', color: '#fff', borderRadius: '999px', '& .MuiChip-label': { px: 0.75 }, mt: 0.25 }} />
              )}
            </Box>
            <Box sx={{ gridColumn: '1 / -1', pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Insurance</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Carrier</Typography>
              <Typography variant="body2">{(horse as any).insuranceCarrier || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Policy #</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{(horse as any).insurancePolicyNumber || '—'}</Typography>
            </Box>
            {(horse as any).status === 'DECEASED' && (
              <>
                <Box sx={{ gridColumn: '1 / -1', pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Deceased</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Date of Death</Typography>
                  {/* Horse.dateOfDeath is a CALENDAR day (UTC midnight from a raw yyyy-MM-dd) — date part only. */}
                  <Typography variant="body2" data-testid="patient-date-of-death">{formatCalendarDate((horse as any).dateOfDeath, 'MMM d, yyyy') || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Cause of Death</Typography>
                  <Typography variant="body2">{(horse as any).causeOfDeath || '—'}</Typography>
                </Box>
              </>
            )}
            <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">
                <strong>{allRecords.length + allSoapNotes.length + procedures.length + allGroupedMedications.length + dischargeSummaries.length}</strong> total records
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {/* Appointment.date is an INSTANT — the clinic zone's day, not the browser's. */}
                Last visit: <strong>{formatInstant(lastVisitDate, INSTANT_FORMATS.date) || '—'}</strong>
              </Typography>
            </Box>
          </Box>
          {canEdit && (
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 14 }} />}
              onClick={() => { setIdentityAnchor(null); setAlertsDialogOpen(true); }}
              sx={{ mt: 1.5, textTransform: 'none', fontSize: '0.75rem' }}
            >
              Manage alerts ({medicalAlerts.length})
            </Button>
          )}
        </Popover>

        {/* Owner / trainer popover */}
        <Popover
          open={Boolean(ownerAnchor)}
          anchorEl={ownerAnchor}
          onClose={() => setOwnerAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { mt: 1, p: 2, minWidth: 280, maxWidth: 360, borderRadius: 2 } } }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Owner & trainer</Typography>
          {horse.owners && horse.owners.length > 0 ? (
            <Stack spacing={1.5}>
              {horse.owners.map((owner, i) => (
                <Box key={i}>
                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', fontWeight: 700 }}>
                    {i === 0 ? 'Main Owner' : `Owner ${i + 1}`}
                    {owner.ownershipPercentage != null && horse.owners.length > 1 ? ` · ${owner.ownershipPercentage}%` : ''}
                  </Typography>
                  {/* The owner name is a LINK INTO /clients, whose list read is
                      view:clients (clients.controller.ts:32) and which is itself behind
                      <ScreenRoute>. Without the slug the click landed on a no-access screen,
                      so the name renders as plain text instead of as an invitation. */}
                  {!can.openClient && (
                    <Typography sx={{ mt: 0.5, fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.43 }}>
                      {owner.name}
                    </Typography>
                  )}
                  {can.openClient && (
                  <Tooltip title="View owner page" arrow placement="top">
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => openOwnerPage(owner, i)}
                      aria-label={`View owner page for ${owner.name}`}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 0.5,
                        p: 0,
                        border: 'none',
                        background: 'none',
                        cursor: resolvingOwnerIdx !== null ? 'default' : 'pointer',
                        textAlign: 'left',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        lineHeight: 1.43,
                        color: 'primary.main',
                      }}
                    >
                      {owner.name}
                      {resolvingOwnerIdx === i ? (
                        <CircularProgress size={12} thickness={5} sx={{ ml: 0.25 }} />
                      ) : (
                        <OpenInNewIcon sx={{ fontSize: 13, opacity: 0.7 }} />
                      )}
                    </Link>
                  </Tooltip>
                  )}
                  {owner.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                      <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption">{owner.phone}</Typography>
                    </Box>
                  )}
                  {owner.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                      <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography component="a" href={`mailto:${owner.email}`} variant="caption" sx={{ color: 'primary.main', textDecoration: 'none', wordBreak: 'break-all' }}>{owner.email}</Typography>
                    </Box>
                  )}
                </Box>
              ))}
              {horse.trainers && horse.trainers.length > 0 && (
                <>
                  <Divider />
                  {horse.trainers.map((trainer, i) => (
                    <Box key={i}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', fontWeight: 700 }}>
                        Trainer {horse.trainers!.length > 1 ? i + 1 : ''}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>{trainer.name}</Typography>
                      {trainer.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                          <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption">{trainer.phone}</Typography>
                        </Box>
                      )}
                      {trainer.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                          <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography component="a" href={`mailto:${trainer.email}`} variant="caption" sx={{ color: 'primary.main', textDecoration: 'none', wordBreak: 'break-all' }}>{trainer.email}</Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">No owner information on file.</Typography>
          )}
        </Popover>

        {/* Event detail drawer — slides in when a timeline row is clicked. */}
        <EventDetailDrawer
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          horseId={horseId}
          canEdit={can.editSoapNote}
          canArchive={canArchive}
          onSoapView={handleViewSoapNote}
          onSoapEdit={handleEditSoapNote}
          onSoapMenu={handleSoapNoteMenuOpen}
          onProcedureEdit={handleEditProcedure}
          onProcedureMenu={handleProcedureMenuOpen}
          onVaccinationMenu={handleVaccinationMenuOpen}
          onVaccinationEdit={can.editClinicalRecord ? (v) => { setSelectedEvent(null); setEditingVaccination(v); } : undefined}
          onRecordMenu={handleRecordMenuOpen}
          onRecordEdit={handleEditVisitNote}
          onDischargeOpen={(id) => { setSelectedEvent(null); setSelectedRecordId(id); setDischargeEditMode(false); setShowDischargeDialog(true); }}
          lowStockItems={lowStockItems.map((i: any) => i.name)}
          totalActiveMedications={totalActiveMedications}
          recordAttachmentMap={recordAttachmentMap}
        />

        {/* Record Action Menu */}
        <Popover
          id="record-menu"
          open={Boolean(anchorPos)}
          anchorReference="anchorPosition"
          anchorPosition={anchorPos || undefined}
          onClose={handleRecordMenuClose}
        >
          <List sx={{ py: 0, minWidth: 200 }}>
            {(() => {
              const soapNote = soapNotes.find(s => s.id === selectedRecordId);
              const procedure = procedures.find(p => p.id === selectedRecordId);
              const vaccination = filteredVaccinations.find((v: any) => v.id === selectedRecordId);
              const medRecord = records.find(r => r.id === selectedRecordId);
              
              if (soapNote) {
                return (
                  <>
                    {canArchive && (
                      <ListItemButton 
                        onClick={() => {
                          if (selectedRecordId) handleArchiveSoapNote(selectedRecordId);
                        }}
                        sx={{ color: 'error.main' }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                          <ArchiveIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Archive SOAP Note" />
                      </ListItemButton>
                    )}
                  </>
                );
              }

              if (procedure) {
                return (
                  <>
                    {canArchive && (
                      <ListItemButton 
                        onClick={() => {
                          if (selectedRecordId) handleArchiveProcedure(selectedRecordId);
                        }}
                        sx={{ color: 'error.main' }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                          <ArchiveIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Archive Procedure" />
                      </ListItemButton>
                    )}
                  </>
                );
              }
              
              if (vaccination) {
                return (
                  <>
                    {canArchive && (
                      <ListItemButton 
                        onClick={() => {
                          if (selectedRecordId) handleArchiveVaccination(selectedRecordId);
                        }}
                        sx={{ color: 'error.main' }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                          <ArchiveIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Archive Vaccination" />
                      </ListItemButton>
                    )}
                  </>
                );
              }

              if (medRecord) {
                return (
                  <>
                    {canArchive && (
                      <ListItemButton 
                        onClick={() => {
                          if (selectedRecordId) handleArchiveRecord(selectedRecordId);
                        }}
                        sx={{ color: 'error.main' }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                          <ArchiveIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Archive Record" />
                      </ListItemButton>
                    )}
                  </>
                );
              }
              
              return null;
            })()}
          </List>
        </Popover>

        {/* Alert Management Dialog */}
        {horse && (
          <AlertManagementDialog
            open={alertsDialogOpen}
            onClose={() => setAlertsDialogOpen(false)}
            horseId={horseId!}
            horseName={horse.name}
          />
        )}

        {/* Discharge Summary Dialog */}
        {showDischargeDialog && horse && (
          <Dialog
            open={showDischargeDialog}
            onClose={() => setShowDischargeDialog(false)}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                m: { xs: 0, sm: 2 },
                maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' },
              },
            }}
          >
            <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              {selectedRecordId ? 'Discharge Summary' : 'Discharge Summaries'}
            </DialogTitle>
            <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
              <DischargeSummarySimplified
                horseId={horse!.id}
                initialSummaryId={selectedRecordId ?? undefined}
                initialEditMode={dischargeEditMode}
              />
            </DialogContent>
            <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Button 
                onClick={() => setShowDischargeDialog(false)}
                fullWidth={isMobile}
                sx={{ minHeight: 44 }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Invoice Notification Snackbar */}
        <Dialog
          open={invoiceNotification.open}
          onClose={() => setInvoiceNotification({ ...invoiceNotification, open: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {invoiceNotification.type === 'success' ? 'Invoice Created' : 'Error'}
          </DialogTitle>
          <DialogContent>
            <Typography>{invoiceNotification.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInvoiceNotification({ ...invoiceNotification, open: false })}>
              Close
            </Button>
            {invoiceNotification.type === 'success' && invoiceNotification.invoiceId && (
              <Button variant="contained" onClick={handleViewInvoice}>
                View Invoice
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* SOAP Note Dialog */}
        {horse && (
          <SoapNoteDialog
            key={selectedSoapNote?.id || 'new-soap-note'} // Force re-render when note changes
            open={soapDialogOpen}
            onClose={handleCloseSoapDialog}
            horseId={horse!.id}
            horseName={horse!.name}
            horseBreed={formatBreed(horse)}
            existingNote={selectedSoapNote || undefined}
            onSave={handleSoapNoteSave}
          />
        )}

        {/* StableDocs: compile the chart into one PDF and send it. */}
        <ShareRecordsDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          horseId={horseId!}
          horseName={horse?.name}
        />

        {/* Medication Dialog */}
        <MedicationDialog
          open={medicationDialogOpen}
          onClose={() => setMedicationDialogOpen(false)}
          onSave={handleMedicationSave}
          lowStockItems={lowStockItems.map((item: any) => item.name)}
          showAll={clinicSettings?.showAllMedications ?? true}
          catalogItems={medicationCatalog.filter(i => i.isActive && i.clinicId)}
        />

        <LogRefillRequestDialog
          open={logRefillOpen}
          onClose={() => setLogRefillOpen(false)}
          preselectedHorseId={horseId}
          onCreated={() => queryClient.invalidateQueries(['refill-requests'])}
        />

        {/* Procedure Dialog */}
        <ProcedureDialog
          open={procedureDialogOpen}
          onClose={() => {
            setProcedureDialogOpen(false);
            setEditingProcedure(null);
          }}
          onSave={handleProcedureSave}
          currentUserName={profile?.name || user?.displayName || user?.email?.split('@')[0] || ''}
          existingProcedure={editingProcedure}
          showAll={clinicSettings?.showAllProcedures ?? true}
          catalogItems={procedureCatalog.filter(i => i.isActive && i.clinicId)}
        />

        {/* Vaccination Dialog */}
        <VaccinationDialog
          open={vaccinationDialogOpen}
          onClose={() => setVaccinationDialogOpen(false)}
          onSave={handleVaccinationSave}
          currentUserName={profile?.name || user?.displayName || user?.email?.split('@')[0] || ''}
          lowStockItems={lowStockItems.map((item: any) => item.name)}
          showAll={clinicSettings?.showAllVaccinations ?? true}
          catalogItems={vaccinationCatalog.filter(i => i.isActive && i.clinicId)}
        />

        {/* Correct a saved vaccination's lot or dose; the record only (ADR-0047). */}
        <EditVaccinationDialog
          vaccination={editingVaccination}
          horseId={horseId!}
          onClose={() => setEditingVaccination(null)}
        />

        {/* Visit Note Dialog */}
        <VisitNoteDialog
          open={visitNoteDialogOpen}
          onClose={() => {
            setVisitNoteDialogOpen(false);
            setEditingVisitNote(null);
          }}
          onSave={handleVisitNoteSave}
          horseId={horseId!}
          existingRecord={editingVisitNote}
          tagSuggestions={visitNoteTagSuggestions}
        />

        {/* Client Communication Dialog */}
        <ClientCommunicationDialog
          open={clientCommDialogOpen}
          onClose={() => setClientCommDialogOpen(false)}
          onSave={(data) => createClientCommunication.mutateAsync(data)}
          horseId={horseId!}
        />

        {/* Confirmation Dialog */}
        <Dialog open={!!confirmAction} onClose={() => setConfirmAction(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Confirm Action</DialogTitle>
          <DialogContent>
            <Typography>{confirmAction?.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
            <Button
              onClick={() => {
                confirmAction?.onConfirm();
                setConfirmAction(null);
              }}
              variant="contained"
              color="error"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default HorseProfileModern;